import React, { Component, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
// mapControls.css and cloudstyle.css ship with the lazy MapWorkspace chunk,
// and react-datepicker's CSS with the DatePickerLazy chunk, so none of them
// render-block the landing page.
import transactionType from './Functions/Transactiontype';
import jwt_decode from 'jwt-decode';
import serverIp from './Functions/serverConfig';
import {
  hasPass72Access,
  hasPlusMaxAccess,
  isPremium,
  isPro,
  isPass72,
  planTierName
} from './Functions/planTier';
import Frontpage from './Components/FrontPage/Frontpage';
import sendAnalytics from './Functions/analytics';
import { errorToMessage, trackFrontendError } from './Functions/frontendErrorTracker';
import prflag from './images/icons8-puerto-rico-48.png';
import usaflag from './images/icons8-usa-flag-48.png';
import { MapEventGuardLegacy } from './MapUiGuardLegacy';
import { track, trackCustom, trackVirtualPage } from '../src/Functions/pixel';
import {
  clearAnalyticsUser,
  setAnalyticsUser,
  trackBeginCheckout,
  trackEvent,
  trackPageView,
  trackSearchInteraction
} from './Functions/ga4';
import { posthogCaptureException } from './Functions/posthog';
import startBehaviorTracking from './Functions/behaviorTracker';

const DatePicker = lazy(() => import("./Components/DatePickerLazy"));

// react-datepicker 4.16.0 removed its defaultProps (React 18 compat) but
// still invokes these handlers unguarded — any picker rendered without them
// crashes with "props.onFocus is not a function" on focus/blur/keys. Spread
// FIRST on every <DatePicker> so explicit handlers still override.
const DATE_PICKER_SAFE_PROPS = {
  onFocus() {}, onBlur() {}, onKeyDown() {}, onSelect() {}, onInputClick() {},
  onClickOutside() {}, onCalendarOpen() {}, onCalendarClose() {},
  onMonthChange() {}, onYearChange() {}, onDropdownFocus() {}, onInputError() {}
};
const Searchbar = lazy(() => import('./Components/Searchbar/Searchbar'));
const AutoExplore = lazy(() => import('./Components/MapWorkspace/AutoExplore'));
const StatsArea = lazy(() => import("./Components/StatsArea/StatsArea"));
const LoginPage = lazy(() => import('./Components/LoginPage/LoginPage'));
const SignUp = lazy(() => import('./Components/SignupPage/SignUp'));
const AccountPage = lazy(() => import('./Components/AccountPage/AccountPage'));
const StatsAreaLight = lazy(() => import('./Components/StatsArea/StatsAreaLight'));
const StatsAreaMid = lazy(() => import('./Components/StatsArea/StatsAreaMid'));
const ForgotPassword = lazy(() => import('./Components/ForgotPassword/ForgotPassword'));
const DirectCheckout = lazy(() => import('./Components/DirectCheckout/DirectCheckout'));
const Tutorial = lazy(() => import('./Components/TutorialPage/Tutorial'));
const Promo = lazy(() => import('./Components/Promotion/Promo'));
const Terms = lazy(() => import('./Components/Terms/Terms'));
const Privacy = lazy(() => import('./Components/Privacy/Privacy'));
const Prompt = lazy(() => import('../src/Components/TutorialPrompt/Prompt'));
const TrialPromt = lazy(() => import('../src/Components/TutorialPrompt/TrialPrompt'));
const PlusMaxPrompt = lazy(() => import('./Components/TutorialPrompt/PlusMaxPrompt'));
const Stats = lazy(() => import('./Components/Stats/Stats'));
const MunicipioStats = lazy(() => import('./Components/Stats/MunicipioStats'));
const MapWorkspace = lazy(() => import('./Components/MapWorkspace/MapWorkspace'));
const AreaPulse = lazy(() => import('./Components/AreaPulse/AreaPulse'));
const GoogleOAuthProvider = lazy(() => import('@react-oauth/google').then((module) => ({ default: module.GoogleOAuthProvider })));
const VERIFY_TOKEN_TIMEOUT_MS = 12000;

const formatDateYYYYMMDD = (value) => {
  const date = new Date(Math.abs(Number(value)));
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDateMs = (value) => {
  const formatted = formatDateYYYYMMDD(value);
  return formatted ? new Date(formatted).getTime() : 0;
};

const toDateObject = (value) => {
  const formatted = formatDateYYYYMMDD(value);
  return formatted ? new Date(formatted) : new Date(0);
};

const SALE_OUTLIER_FILTER_SETTINGS = Object.freeze({
  lowerQuantile: 0.25,
  upperQuantile: 0.75,
  lowerIqrMultiplier: 0.4,
  upperIqrMultiplier: 1.5,
  minSampleSize: 8
});

const taxRates = {
  'Adjuntas': 0.0833,
  'Aguada': 0.0883,
  'Aguadilla': 0.1033,
  'Aguas Buenas': 0.1033,
  'Aibonito': 0.0933,
  'Añasco': 0.0968,
  'Arecibo': 0.1163,
  'Arroyo': 0.1033,
  'Barceloneta': 0.1033,
  'Barranquitas': 0.0933,
  'Bayamón': 0.0958,
  'Cabo Rojo': 0.1008,
  'Caguas': 0.1033,
  'Camuy': 0.1033,
  'Canóvanas': 0.1033,
  'Carolina': 0.1133,
  'Cataño': 0.1033,
  'Cayey': 0.1058,
  'Ceiba': 0.0933,
  'Ciales': 0.1033,
  'Cidra': 0.1233,
  'Coamo': 0.1033,
  'Comerío': 0.0908,
  'Corozal': 0.0983,
  'Culebra': 0.0883,
  'Dorado': 0.1058,
  'Fajardo': 0.1033,
  'Florida': 0.1033,
  'Guánica': 0.1058,
  'Guayama': 0.1008,
  'Guayanilla': 0.1083,
  'Guaynabo': 0.1008,
  'Gurabo': 0.1183,
  'Hatillo': 0.0905,
  'Hormigueros': 0.0983,
  'Humacao': 0.0883,
  'Isabela': 0.0933,
  'Jayuya': 0.0808,
  'Juana Díaz': 0.0933,
  'Juncos': 0.1133,
  'Lajas': 0.1233,
  'Lares': 0.1008,
  'Las Marías': 0.0818,
  'Las Piedras': 0.1033,
  'Loíza': 0.1173,
  'Luquillo': 0.0933,
  'Manatí': 0.0958,
  'Maricao': 0.1033,
  'Maunabo': 0.0983,
  'Mayagüez': 0.1058,
  'Moca': 0.0958,
  'Morovis': 0.1083,
  'Naguabo': 0.0983,
  'Naranjito': 0.0983,
  'Orocovis': 0.0803,
  'Patillas': 0.1033,
  'Peñuelas': 0.0858,
  'Ponce': 0.1233,
  'Quebradillas': 0.0883,
  'Rincón': 0.1083,
  'Río Grande': 0.1033,
  'Sabana Grande': 0.1033,
  'Salinas': 0.1033,
  'San Germán': 0.1008,
  'San Juan': 0.1033,
  'San Lorenzo': 0.1023,
  'San Sebastián': 0.0908,
  'Santa Isabel': 0.1083,
  'Toa Alta': 0.1133,
  'Toa Baja': 0.1183,
  'Trujillo Alto': 0.1058,
  'Utuado': 0.0883,
  'Vega Alta': 0.0868,
  'Vega Baja': 0.1033,
  'Vieques': 0.1183,
  'Villalba': 0.0958,
  'Yabucoa': 0.1083,
  'Yauco': 0.1033
}

class App extends Component{
  constructor(){
    super();
    // ✅ NEW: restore session immediately if a non-expired token exists (trial or normal)
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    let initialLoggedIn = false;
    let initialDecodeToken = '';
    let initialToken = '';
    let initialLoginBtnContainerShow = 'loginBtnContainer';
    // ? NEW: Frontpage landing control (prices)
    let initialFrontpageTarget = "";   // "" | "prices"
    let initialFrontpageReason = "";   // "trial_expired" | "trial_used" | etc
    let initialFrontpageSrc = "";      // "return_visit" | "facebook_ad" | etc
    let initialPageIndex = "";
    let initialDirectCheckoutMagicToken = "";
    let initialDirectCheckoutBilling = "monthly";
    // Map selection mode: auto explore (viewport points + one-click complex
    // selection) vs manual polygon drawing. Auto is now the default for every
    // user; a one-time intro popup tells them they can switch with the tab. A
    // user who has explicitly chosen manual keeps it.
    let initialSelectionMode = "auto";
    try {
      if (localStorage.getItem("cd_map_mode") === "manual") initialSelectionMode = "manual";
    } catch {}

    // ? NEW: allow backend to send user to prices via URL
    try {
      const params = new URLSearchParams(window.location.search);
      const go = String(params.get("go") || "").toLowerCase();
      if (go === "prices" || go === "pricing") {
        initialFrontpageTarget = "prices";
        initialFrontpageReason = params.get("reason") || "";
        initialFrontpageSrc = params.get("src") || "";
      }
      const checkoutAuth = String(params.get("checkoutAuth") || "").trim();
      const checkoutBilling = String(params.get("checkoutBilling") || "").toLowerCase();
      if (checkoutAuth) {
        initialFrontpageTarget = "prices";
        initialPageIndex = "directCheckout";
        initialDirectCheckoutMagicToken = checkoutAuth;
        initialDirectCheckoutBilling = ["annual", "pass72", "pro_monthly", "pro_annual"].includes(checkoutBilling) ? checkoutBilling : "monthly";
      }
    } catch {}
    if (storedToken) {
      try {
        const decoded = jwt_decode(storedToken);
        const expMs = decoded && decoded.exp ? decoded.exp * 1000 : 0;

        const uname = String(decoded?.username || "");
        const init  = String(decoded?.initial || "").toUpperCase();
        const isTrial = uname.startsWith("trial-") || init === "TEMPORARY";
        const isPass = isPass72(decoded);

        if (!expMs || Date.now() < expMs) {
          initialLoggedIn = true;
          initialDecodeToken = decoded;
          initialToken = storedToken;
          initialLoginBtnContainerShow = 'loginBtnContainerHide';
        } else {
          // expired -> clear
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');

          // ? IMPORTANT: if it was a TRIAL, force prices on return visit
          if (isTrial && !initialFrontpageTarget) {
            initialFrontpageTarget = "prices";
            initialFrontpageReason = "trial_expired";
            initialFrontpageSrc = "return_visit";
          }
          else if (isPass && !initialFrontpageTarget) {
            initialFrontpageTarget = "prices";
            initialFrontpageReason = "pass_expired";
            initialFrontpageSrc = "return_visit";
          }
        }
      } catch (e) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
      }
    }

    // Initialize the state variables
    this.state={
      initialCoords:[18.233682, -66.467499], // Initialize the map over Puerto Rico
      initialZoomOut:9,
      maxZoomOutLimit:9,
      zoom:16,
      zoomApropriate:true,
      setCoords:"",
      dataRegister:[],
      filteredDataRegister:[],
      llmFilterMode:false,
      llmRenderVersion:0,
      fetchingStatus:"",
      pointsPaneClass:'fade-in',
      maxPrice:999999999,
      minPrice:0,
      startDate:'',
      endDate: '',
      startDateRepo:'',
      endDateRepo: '',
      startDateToDisplay:'',
      endDateToDisplay:'',
      startDateToDisplayRepo:'',
      endDateToDisplayRepo:'',
      repoStartDatePickerOpen:false,
      repoEndDatePickerOpen:false,
      minStruct:0,
      maxStruct:999999999,
      sendBoxLimitsHide:true,
      filterOutliers:true,
      mapDraggable:true,
      errorActive:'inactive',
      pageIndex:initialPageIndex,
      serverIp:serverIp,
      loginBtnContainerShow: initialLoginBtnContainerShow,
      loggedIn: initialLoggedIn,
      token: initialToken,
      decodeToken: initialDecodeToken,
      floodZoneWarning:'',
      remainingCoolDown:'',
      subscriptionIndex:'',
      toggleBg:'toggleInactive',
      repoData:[],
      userFirstLogin:true,
      paidTierAtInit:false,
      selectedPayment:'',
      directCheckoutMagicToken: initialDirectCheckoutMagicToken,
      directCheckoutBilling: initialDirectCheckoutBilling,
      selectedLanguage:'spanish',
      searchPlaceholder:'Buscar Direccion Aquí',
      fingerprint:'',
      trials:0,
      closeBehavior:false,
      masterDataRegister: [],
      trialTutorialFlag:false,
      selectionMode: initialSelectionMode,
      autoComplex: null,
      autoComplexLoading: false,
      autoPointsLoading: false,
      // One-time popup explaining the Auto/Manual tab. Shown once per device
      // (localStorage flag), never nags again after acknowledgement.
      mapModeIntroSeen: (() => {
        try { return localStorage.getItem('cd_map_mode_intro_seen') === '1'; } catch { return true; }
      })(),
      frontpageTarget: initialFrontpageTarget,
      frontpageReason: initialFrontpageReason,
      frontpageSrc: initialFrontpageSrc,
      geoLocating: false,
      geoLocateError: "",
      gpsFocusPoint: null,
      authNoticeMessage:'',
      authNoticeTone:'info',
      emailVerificationProcessing:false,
      emailVerificationRefreshPending:false,
      authNoticeExpanded:true,
      // ---- PlusMAX Pro: saved areas + Area Pulse ----
      proPulse:null,
      savedAreas:[],
      showAreaPulse:false,
      areaPulseView:'pulse',
      lastSearchPolygon:null,
      saveAreaPromptOpen:false,
      saveAreaName:'',
      saveAreaStatus:'',
      showSaveAreaHint:false
      }
    this.mapRef = React.createRef();
    // this.handleMouseOver = this.handleMouseOver.bind(this);
    this.processLastVisit()
    this.markerRefs = {};
    this.lastTrackedView = '';
    this.lastTrackedMarkerId = '';
    this.lastTrackedRoute = '';
    this.behaviorTracker = null;
    this.sessionStartedAt = Date.now();
	    this.firstSearchTracked = false;
	    this.lastFilterTrackAt = 0;
	    this.lastMapViewportSignature = '';
	    this.gpsErrorTimer = null;
	    this.gpsMarkerTimer = null;
	    this.futuristicPressFeedbackHandler = null;
	    this.pressFeedbackTimers = new Set();
	    this.outlierFilterCache = {
	      rowsRef: null,
	      cacheKey: '',
	      summary: null
	    };

    this.handleProgrammaticClick = this.handleProgrammaticClick.bind(this);
    this.pulseFetchedForSession = false;
    this.pulsePolygonLayer = null;
    // Ref to the leaflet-draw FeatureGroup so rehydrated saved-area polygons
    // join it and the native "Borrar Selección" toolbar button activates.
    this.drawFeatureGroupRef = React.createRef();
  }

	    renderDeferred(node, fallback = null) {
	      return <Suspense fallback={fallback}>{node}</Suspense>;
	    }

	    renderPageTransition(node, routeName, fallback = null) {
	      const normalizedRoute = String(routeName || this.state.pageIndex || "panel")
	        .replace(/[^a-z0-9_-]/gi, "")
	        .toLowerCase() || "panel";

	      return (
	        <div
	          key={`futuristic-page-${normalizedRoute}`}
	          className={`futuristicPageSwitch futuristicPageSwitch--${normalizedRoute}`}
	        >
	          <div className='futuristicPageSwitchGlow' aria-hidden='true'></div>
	          <div className='futuristicPageSwitchInner'>
	            {this.renderDeferred(node, fallback)}
	          </div>
	        </div>
	      );
	    }

	    installFuturisticPressFeedback() {
	      if (typeof document === "undefined" || this.futuristicPressFeedbackHandler) return;

	      const pressTargets = [
	        "button:not(:disabled)",
	        "[role='button']",
	        ".logOutBtn",
	        ".pageCloseBtn",
	        ".closeBtn",
	        ".closeBtnPromo",
	        ".closeBtnPay",
	        ".membershipBox button",
	        ".submit-button",
	        ".submit-button2",
	        ".submit-button3",
	        ".forgotPasswordLink",
	        ".karibe-doc-btn",
	        ".authNoticeButton",
	        ".cdcoPrimaryBtn",
	        ".cdcoSecondaryBtn",
	        ".cdcoCloseBtn",
	        ".cdcoLinkBtn",
	        ".fetchData",
	        ".searchBottom",
	        ".searchCoordsBtn",
	        ".sendBoxMinimize",
	        ".statButton",
	        ".minimizeBtn",
	        ".statsHeaderBtn",
	        ".statsShowMinimized",
	        ".listElement",
	        ".levelShow3",
	        ".language-selector2 button",
	        ".toggleTrack",
	        ".toggleTrackDir",
	        ".leaflet-control a",
	        ".toggleBtn",
	        ".gpsLocateBtn"
	      ].join(",");

	      this.futuristicPressFeedbackHandler = (event) => {
	        const target = event.target?.closest ? event.target.closest(pressTargets) : null;
	        if (!target || target.disabled || target.getAttribute("aria-disabled") === "true") return;

	        target.classList.remove("futuristicPressActive");
	        void target.offsetWidth;
	        target.classList.add("futuristicPressActive");

	        const timer = window.setTimeout(() => {
	          try { target.classList.remove("futuristicPressActive"); } catch {}
	          this.pressFeedbackTimers.delete(timer);
	        }, 360);
	        this.pressFeedbackTimers.add(timer);
	      };

	      document.addEventListener("pointerdown", this.futuristicPressFeedbackHandler, { passive: true });
	    }

	    removeFuturisticPressFeedback() {
	      try {
	        if (this.futuristicPressFeedbackHandler && typeof document !== "undefined") {
	          document.removeEventListener("pointerdown", this.futuristicPressFeedbackHandler);
	        }
	      } catch {}
	      this.futuristicPressFeedbackHandler = null;
	      try {
	        this.pressFeedbackTimers.forEach((timer) => window.clearTimeout(timer));
	        this.pressFeedbackTimers.clear();
	      } catch {}
	    }


	    handleProgrammaticClick(uniqueId) {
      const marker = this.markerRefs[uniqueId];
      if (marker) {
        // Fire the 'click' to show popup or highlight
        if (marker.leafletElement) {
          // React Leaflet v2/v3 style
          marker.leafletElement.fire("click");
        } else {
          // React Leaflet v4 style
          marker.fire("click");
        }
    
        // Get the marker’s lat/lng
        const latLng = marker.getLatLng();
        // Now pan the map with a slight upward offset (e.g. 150px)
        this.panMapWithOffset(latLng, -170);
      }
    }
    
    /**
     * Moves the map center to `latLng`, but offset upward by `offsetPx`.
     */
    panMapWithOffset(latLng, offsetPx) {
      // Leaflet Map instance from the MapContainer ref
      const map = this.mapRef.current;
    
      // 1) Figure out the current zoom
      const currentZoom = map.getZoom();
    
      // 2) Convert latLng to a pixel point at current zoom
      const point = map.project(latLng, currentZoom);
    
      // 3) Subtract the vertical offset from the point
      //    so it appears higher on the screen
      const offsetPoint = point.subtract([0, offsetPx]);
    
      // 4) Convert that offset point back to lat/lng
      const newLatLng = map.unproject(offsetPoint, currentZoom);
    
      // 5) Center the map on the new lat/lng
      map.setView(newLatLng, currentZoom, { animate: true });
    }
    
    getCookie(name) {
      try {
        const escaped = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
        const m = document.cookie.match(new RegExp("(^|;\\s*)" + escaped + "=([^;]*)"));
        return m ? decodeURIComponent(m[2]) : null;
      } catch {
        return null;
      }
    }
  
    clearCookie(name) {
      // try non-secure + secure delete (covers both http dev + https prod)
      document.cookie = `${name}=; Max-Age=0; path=/; samesite=Lax`;
      document.cookie = `${name}=; Max-Age=0; path=/; samesite=Lax; secure`;
    }

    buildCleanLandingUrl(params) {
      try {
        const stripKeys = new Set(["trialToken", "fromTrial", "go", "reason", "src", "verifyEmail"]);
        const preserved = new URLSearchParams();
        params.forEach((value, key) => {
          if (!stripKeys.has(String(key))) preserved.append(String(key), String(value));
        });
        const qs = preserved.toString();
        const hash = window.location.hash || "";
        return `${window.location.origin}${window.location.pathname}${qs ? `?${qs}` : ""}${hash}`;
      } catch {
        return `${window.location.origin}${window.location.pathname}${window.location.hash || ""}`;
      }
    }

    persistBootstrapToken(token){
      if(!token){return;}
      try{
        const hadLocal = Boolean(localStorage.getItem('token'));
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        if(hadLocal===true){
          localStorage.setItem('token', token);
        }
        else{
          sessionStorage.setItem('token', token);
        }
      }catch{}
    }

    formatVerificationDeadline(unixTs){
      const ts = Number(unixTs);
      if(!Number.isFinite(ts) || ts <= 0){
        return '';
      }
      try{
        return new Date(ts * 1000).toLocaleString(
          this.state.selectedLanguage === 'english' ? 'en-US' : 'es-PR',
          {
            year:'numeric',
            month:'short',
            day:'numeric',
            hour:'numeric',
            minute:'2-digit'
          }
        );
      }catch{
        return '';
      }
    }

    requestVerificationEmail = () => {
      let token = '';
      try{
        token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      }catch{}
      if(!token){
        return;
      }
      trackEvent('email_verification_link_requested', { source: 'app_banner' });
      fetch(`${this.state.serverIp}/api/email-verification/start`, {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          language: this.state.selectedLanguage
        })
      })
      .then((res)=>res.json())
      .then((data)=>{
        if(data?.status === 'sent'){
          trackEvent('email_verification_link_sent', { source: 'app_banner' });
          this.setState({
            authNoticeMessage: this.languageIndexer(
              'Te enviamos otro enlace. Busca el email de CASADATA y toca el enlace seguro para verificar tu cuenta.',
              'We sent another verification link. Open the CASADATA email and tap the secure link to verify your account.'
            ),
            authNoticeTone:'success',
            authNoticeExpanded:true
          });
        }
        else if(data?.status === 'already_verified'){
          trackEvent('email_verification_already_complete', { source: 'app_banner' });
          this.setState({
            authNoticeMessage: this.languageIndexer(
              'Tu email ya esta verificado.',
              'Your email is already verified.'
            ),
            authNoticeTone:'success',
            authNoticeExpanded:true
          });
        }
      })
      .catch((error)=>{
        trackEvent('email_verification_link_failed', { source: 'app_banner' });
        this.reportFrontendError("verification_email_request_failed", error, {
          source: "app_banner",
          endpoint: "/api/email-verification/start"
        });
      });
    }

    refreshVerifiedAccess = () => {
      let token = '';
      try{
        token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      }catch{}
      if(!token || this.state.emailVerificationRefreshPending === true){
        return;
      }
      trackEvent('email_verification_status_check_started', { source: 'app_banner' });
      this.setState({
        emailVerificationRefreshPending:true,
        authNoticeMessage:'',
        authNoticeTone:'info',
        authNoticeExpanded:true
      });
      fetch(`${this.state.serverIp}/api/email-verification/refresh`, {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          fingerprint: this.state.fingerprint || ''
        })
      })
      .then((res)=>res.json())
      .then((data)=>{
        if(data?.status === 'verified' && data?.response){
          trackEvent('email_verification_completed', { source: 'status_check' });
          this.persistBootstrapToken(data.response);
          this.setState({
            emailVerificationRefreshPending:false,
            authNoticeMessage: this.languageIndexer(
              'Tu email ya esta verificado. Su cuenta esta completada.',
              'Your email is verified. Your account is not complete.'
            ),
            authNoticeTone:'success',
            authNoticeExpanded:true
          });
          this.verifyToken();
          try { this.fetchTutorialPrompt() } catch {}
          try { this.fetchTrials() } catch {}
          try { this.decodeToken() } catch {}
          return;
        }

        if(data?.status === 'pending'){
          trackEvent('email_verification_still_pending', { source: 'status_check' });
          this.setState({
            emailVerificationRefreshPending:false,
            authNoticeMessage: this.languageIndexer(
              'Todavia no vemos la verificacion completada. Abre el email de CASADATA y toca el enlace seguro.',
              'We still do not see the verification completed. Open the CASADATA email and tap the secure link.'
            ),
            authNoticeTone:'info',
            authNoticeExpanded:true
          });
          return;
        }

        throw new Error(data?.error || 'Verification refresh failed.');
      })
      .catch((error)=>{
        trackEvent('email_verification_status_check_failed', { source: 'app_banner' });
        this.setState({
          emailVerificationRefreshPending:false,
          authNoticeMessage: this.languageIndexer(
            'No pudimos actualizar el estado de verificacion ahora mismo. Intenta otra vez en unos segundos.',
            'We could not refresh the verification status right now. Try again in a few seconds.'
          ),
          authNoticeTone:'error',
          authNoticeExpanded:true
        });
        this.reportFrontendError("verification_status_refresh_failed", error, {
          source: "app_banner",
          endpoint: "/api/email-verification/refresh"
        });
      });
    }

    consumeEmailVerificationToken = (verificationToken) => {
      if(!verificationToken){
        return;
      }
      trackEvent('email_verification_token_submitted', { source: 'verification_link' });
      this.setState({
        emailVerificationProcessing:true,
        emailVerificationRefreshPending:false,
        authNoticeMessage:'',
        authNoticeTone:'info',
        authNoticeExpanded:true
      });
      fetch(`${this.state.serverIp}/api/email-verification/verify`, {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
        },
        body: JSON.stringify({
          token: verificationToken
        })
      })
      .then((res)=>res.json())
      .then((data)=>{
        if(!data?.response){
          throw new Error(data?.error || 'Verification failed.');
        }
        this.persistBootstrapToken(data.response);
        trackEvent('email_verification_completed', { source: 'verification_link' });
        this.setState({
          emailVerificationProcessing:false,
          emailVerificationRefreshPending:false,
          authNoticeMessage: this.languageIndexer(
            'Tu email fue verificado. Ya tienes acceso completo.',
            'Your email was verified. Full access is now unlocked.'
          ),
          authNoticeTone:'success',
          authNoticeExpanded:true
        });
        this.verifyToken();
        try { this.fetchTutorialPrompt() } catch {}
        try { this.fetchTrials() } catch {}
        try { this.decodeToken() } catch {}
      })
      .catch((error)=>{
        trackEvent('email_verification_failed', { source: 'verification_link' });
        this.setState({
          emailVerificationProcessing:false,
          emailVerificationRefreshPending:false,
          authNoticeMessage: this.languageIndexer(
            'No se pudo completar la verificacion del email. Solicita un enlace nuevo.',
            'Email verification could not be completed. Request a new link.'
          ),
          authNoticeTone:'error',
          authNoticeExpanded:true
        });
        this.reportFrontendError("email_verification_bootstrap_failed", error, {
          source: "app_mount",
          endpoint: "/api/email-verification/verify"
        });
      });
    }

    renderVerificationCoach(){
      const decoded = this.state.decodeToken;
      const verificationPending = Boolean(decoded && decoded.emailVerified === false);
      const processing = this.state.emailVerificationProcessing === true;
      const refreshPending = this.state.emailVerificationRefreshPending === true;
      if(processing !== true && refreshPending !== true && this.state.authNoticeMessage === '' && verificationPending === false){
        return null;
      }

      const graceText = verificationPending && decoded?.verificationGraceUntil
        ? this.formatVerificationDeadline(decoded.verificationGraceUntil)
        : '';
      const email = String(decoded?.username || '').trim();
      const detailsExpanded = verificationPending ? this.state.authNoticeExpanded !== false : true;
      const tone = processing === true
        ? 'info'
        : (verificationPending ? 'pending' : (this.state.authNoticeTone || 'info'));
      const headline = processing === true
        ? this.languageIndexer('Verificando tu email...', 'Verifying your email...')
        : verificationPending
          ? this.languageIndexer('Accion requerida: verifica tu email', 'Action required: verify your email')
          : this.state.authNoticeTone === 'success'
            ? this.languageIndexer('Email verificado', 'Email verified')
            : this.state.authNoticeTone === 'error'
              ? this.languageIndexer('No pudimos completar la verificacion', 'We could not complete verification')
              : this.languageIndexer('Revisa tu email', 'Check your email');
      const bodyMessage = verificationPending
        ? this.languageIndexer(
            `Tu cuenta sigue provisional. Abre el email de CASADATA${email ? ` enviado a ${email}` : ''} y toca el enlace seguro para desbloquear acceso completo. Exportaciones, cambios de cuenta y futuros logins seguiran pidiendo esta verificacion.${graceText ? ` El acceso premium provisional vence el ${graceText}.` : ''}`,
            `Your account is still provisional. Open the CASADATA email${email ? ` sent to ${email}` : ''} and tap the secure link to unlock full access. Exports, account changes, and future logins will keep requiring this verification.${graceText ? ` Provisional premium access ends on ${graceText}.` : ''}`
          )
        : (this.state.authNoticeMessage || '');
      const compactMessage = verificationPending
        ? this.languageIndexer(
            'Abre el email de CASADATA y toca el enlace seguro para verificar tu cuenta.',
            'Open the CASADATA email and tap the secure link to verify your account.'
          )
        : bodyMessage;
      const statusMessage = verificationPending && processing !== true
        ? (refreshPending === true
          ? this.languageIndexer(
              'Estamos revisando si ya completaste la verificacion...',
              'We are checking whether you already completed verification...'
            )
          : this.state.authNoticeMessage)
        : '';
      const verificationSteps = verificationPending
        ? [
            this.languageIndexer(
              'Busca en tu inbox un email de CASADATA con el asunto "Verifica tu email de CASADATA".',
              'Look in your inbox for the CASADATA message with the subject "Verify your CASADATA email".'
            ),
            this.languageIndexer(
              email ? `Abre el email enviado a ${email}.` : 'Abre el email mas reciente de CASADATA.',
              email ? `Open the email sent to ${email}.` : 'Open the latest CASADATA email.'
            ),
            this.languageIndexer(
              'Toca el enlace seguro del email y vuelve aqui. El acceso completo se activa de inmediato.',
              'Tap the secure link in that email and come back here. Full access unlocks right away.'
            )
          ]
        : [];

      return(
        <div className='authNoticeShell'>
          <div className={`authNoticeCard authNoticeCard--${tone}${detailsExpanded ? '' : ' authNoticeCard--compact'}`}>
            <div className='authNoticeHeader'>
              <div className='authNoticeCopy'>
                {verificationPending ? (
                  <div className='authNoticeEyebrow'>
                    {this.languageIndexer('Verificacion pendiente', 'Verification pending')}
                  </div>
                ) : null}
                <div className='authNoticeTitle'>{headline}</div>
                <div className='authNoticeBody'>{detailsExpanded ? bodyMessage : compactMessage}</div>
                {verificationPending ? (
                  <div className='authNoticeMeta'>
                    {email ? (
                      <div className='authNoticeChip'>
                        {this.languageIndexer(`Email: ${email}`, `Email: ${email}`)}
                      </div>
                    ) : null}
                    {graceText ? (
                      <div className='authNoticeChip'>
                        {this.languageIndexer(
                          `Premium provisional hasta ${graceText}`,
                          `Provisional premium until ${graceText}`
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {!verificationPending && processing !== true ? (
                <button
                  type='button'
                  className='authNoticeDismiss'
                  onClick={()=>this.setState({authNoticeMessage:'',authNoticeTone:'info'})}
                  aria-label={this.languageIndexer('Cerrar aviso', 'Dismiss notice')}
                >
                  x
                </button>
              ) : null}
            </div>

            {statusMessage ? (
              <div className={`authNoticeStatus authNoticeStatus--${this.state.authNoticeTone || 'info'}`}>
                {statusMessage}
              </div>
            ) : null}

            {verificationPending && detailsExpanded ? (
              <div className='authNoticeSteps'>
                {verificationSteps.map((step, index)=>(
                  <div className='authNoticeStep' key={`verification-step-${index}`}>
                    <div className='authNoticeStepIndex'>{index + 1}</div>
                    <div className='authNoticeStepText'>{step}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {verificationPending && processing !== true ? (
              <div className='authNoticeActions'>
                <button
                  type='button'
                  className='authNoticeButton authNoticeButton--primary'
                  onClick={this.requestVerificationEmail}
                  disabled={refreshPending === true}
                >
                  {this.languageIndexer('Reenviar email de verificacion', 'Resend verification email')}
                </button>
                <button
                  type='button'
                  className='authNoticeButton authNoticeButton--secondary'
                  onClick={this.refreshVerifiedAccess}
                  disabled={refreshPending === true}
                >
                  {refreshPending === true
                    ? this.languageIndexer('Revisando...', 'Checking...')
                    : this.languageIndexer('Ya verifique mi email', 'I already verified my email')}
                </button>
                <button
                  type='button'
                  className='authNoticeButton authNoticeButton--ghost'
                  onClick={()=>this.setState({authNoticeExpanded: !detailsExpanded})}
                >
                  {detailsExpanded
                    ? this.languageIndexer('Ocultar detalles', 'Hide details')
                    : this.languageIndexer('Mostrar pasos', 'Show steps')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    renderAuthNotice(){
      const decoded = this.state.decodeToken;
      const verificationPending = Boolean(decoded && decoded.emailVerified === false);
      if(this.state.emailVerificationProcessing !== true && this.state.authNoticeMessage === '' && verificationPending === false){
        return null;
      }

      const graceText = verificationPending && decoded?.verificationGraceUntil
        ? this.formatVerificationDeadline(decoded.verificationGraceUntil)
        : '';
      const pendingMessage = verificationPending
        ? this.languageIndexer(
            `Tu cuenta sigue provisional. Verifica tu email para exportar datos, manejar tu cuenta y entrar de nuevo.${graceText ? ` El acceso premium provisional vence el ${graceText}.` : ''}`,
            `Your account is still provisional. Verify your email to export data, manage your account, and sign in again.${graceText ? ` Provisional premium access ends on ${graceText}.` : ''}`
          )
        : '';
      const message = this.state.emailVerificationProcessing === true
        ? this.languageIndexer('Verificando tu email...', 'Verifying your email...')
        : (this.state.authNoticeMessage || pendingMessage);
      const tone = this.state.emailVerificationProcessing === true ? 'info' : (this.state.authNoticeTone || 'info');
      const background = tone === 'success'
        ? 'rgba(15, 118, 110, 0.96)'
        : (tone === 'error'
          ? 'rgba(153, 27, 27, 0.97)'
          : 'rgba(17, 24, 39, 0.96)');

      return(
        <div
          style={{
            position:'fixed',
            top:14,
            left:'50%',
            transform:'translateX(-50%)',
            zIndex:5000,
            width:'min(92vw, 760px)',
            background,
            color:'#fff',
            borderRadius:18,
            boxShadow:'0 14px 36px rgba(0,0,0,.24)',
            padding:'14px 18px',
            display:'flex',
            alignItems:'center',
            gap:12
          }}
        >
          <div style={{flex:1,fontSize:14,lineHeight:1.45}}>{message}</div>
          {verificationPending && this.state.emailVerificationProcessing !== true ? (
            <button
              type='button'
              onClick={this.requestVerificationEmail}
              style={{
                border:'1px solid rgba(255,255,255,.22)',
                background:'rgba(255,255,255,.1)',
                color:'#fff',
                borderRadius:999,
                padding:'9px 14px',
                fontWeight:700,
                cursor:'pointer'
              }}
            >
              {this.languageIndexer('Reenviar enlace', 'Resend link')}
            </button>
          ) : null}
          {this.state.emailVerificationProcessing !== true ? (
            <button
              type='button'
              onClick={()=>this.setState({authNoticeMessage:'',authNoticeTone:'info'})}
              style={{
                border:'none',
                background:'transparent',
                color:'#fff',
                fontSize:18,
                cursor:'pointer'
              }}
              aria-label={this.languageIndexer('Cerrar aviso', 'Dismiss notice')}
            >
              ×
            </button>
          ) : null}
        </div>
      );
    }
  
    isTrialDecoded(decoded) {
      try {
        const uname = String(decoded?.username || "");
        const init  = String(decoded?.initial || "").toUpperCase();
        return uname.startsWith("trial-") || init === "TEMPORARY";
      } catch {
        return false;
      }
    }

    isPassDecoded(decoded) {
      try {
        return isPass72(decoded);
      } catch {
        return false;
      }
    }

    getPlanTierFromDecoded(decoded) {
      if (!decoded) return "Base";
      const tier = planTierName(decoded);
      if (tier !== "Base") return tier;
      if (this.isTrialDecoded(decoded)) return "Trial";
      return "Base";
    }

    syncAnalyticsUser(decoded) {
      if (!decoded) {
        clearAnalyticsUser();
        return;
      }
      setAnalyticsUser({
        userId: decoded.id || decoded.sub || "",
        planTier: this.getPlanTierFromDecoded(decoded),
        isTrial: this.isTrialDecoded(decoded),
        language: this.state.selectedLanguage
      });
    }

    getActiveViewName() {
      if (this.state.loggedIn === true && this.state.pageIndex === "") {
        return "Map Workspace";
      }
      return "";
    }

    trackActiveView() {
      const view = this.getActiveViewName();
      if (!view || this.lastTrackedView === view) return;

      this.lastTrackedView = view;
      trackPageView(view, {
        app_surface: String(view).toLowerCase().replace(/[^a-z0-9]+/g, "_")
      });
    }

    getCurrentRouteName() {
      if (this.state.loggedIn === true && this.state.pageIndex === "") {
        return "map_workspace";
      }
      if (this.state.pageIndex && String(this.state.pageIndex).trim() !== "") {
        return String(this.state.pageIndex).toLowerCase();
      }
      return this.state.loggedIn ? "map_workspace" : "frontpage";
    }

    trackAppRouteAndFunnel(source = "state_update") {
      const route = this.getCurrentRouteName();
      const planTier = this.getPlanTierFromDecoded(this.state.decodeToken);
      const common = {
        route,
        source,
        logged_in: Boolean(this.state.loggedIn),
        plan_tier: planTier
      };

      if (this.behaviorTracker) {
        this.behaviorTracker.trackVirtualPage(route, common);
      }

      if (this.lastTrackedRoute === route) return;
      this.lastTrackedRoute = route;

      trackVirtualPage(route, {
        source,
        logged_in: Boolean(this.state.loggedIn),
        plan_tier: planTier
      });

      if (!this.behaviorTracker) return;
      if (this.state.loggedIn) {
        this.behaviorTracker.trackFunnelStage("authenticated_session", { plan_tier: planTier });
      } else {
        this.behaviorTracker.trackFunnelStage("anonymous_session");
      }

      if (route === "login") this.behaviorTracker.trackFunnelStage("auth_login_viewed");
      if (route === "signup") this.behaviorTracker.trackFunnelStage("auth_signup_viewed");
      if (route === "account") this.behaviorTracker.trackFunnelStage("pricing_consideration");
      if (route === "upgradepromotion") this.behaviorTracker.trackFunnelStage("upgrade_prompt_seen");
      if (route === "plusmaxprompt") this.behaviorTracker.trackFunnelStage("plusmax_prompt_seen");
      if (route === "tutorial") this.behaviorTracker.trackFunnelStage("tutorial_viewed");
      if (route === "stats") this.behaviorTracker.trackFunnelStage("market_stats_viewed");
    }

    trackFilterAdjustments(prevState) {
      const changed = [];
      if (prevState.minPrice !== this.state.minPrice) changed.push("min_price");
      if (prevState.maxPrice !== this.state.maxPrice) changed.push("max_price");
      if (prevState.minStruct !== this.state.minStruct) changed.push("min_structure");
      if (prevState.maxStruct !== this.state.maxStruct) changed.push("max_structure");
      if (prevState.startDate !== this.state.startDate) changed.push("start_date");
      if (prevState.endDate !== this.state.endDate) changed.push("end_date");
      if (prevState.filterOutliers !== this.state.filterOutliers) changed.push("outlier_filter");
      if (!changed.length) return;

      const now = Date.now();
      if (now - this.lastFilterTrackAt < 900) return;
      this.lastFilterTrackAt = now;

      trackEvent("search_filters_changed", {
        changed_fields: changed.join("|"),
        min_price: Number(this.state.minPrice) || 0,
        max_price: Number(this.state.maxPrice) || 0,
        min_structure: Number(this.state.minStruct) || 0,
        max_structure: Number(this.state.maxStruct) || 0,
        filter_outliers: Boolean(this.state.filterOutliers)
      });

      if (this.behaviorTracker) {
        this.behaviorTracker.trackCustomAction("db_filter_change", {
          changed_count: changed.length,
          changed_fields: changed.join("|")
        });
      }
    }

    trackMapViewport(interactionType = "moveend", mapInstance = null) {
      const map = mapInstance || this.mapRef.current;
      if (!map) return;
      try {
        const center = map.getCenter ? map.getCenter() : null;
        const zoom = map.getZoom ? map.getZoom() : null;
        const centerLat = center ? Number(center.lat).toFixed(2) : "";
        const centerLng = center ? Number(center.lng).toFixed(2) : "";
        const signature = `${interactionType}|${zoom}|${centerLat}|${centerLng}`;
        if (signature === this.lastMapViewportSignature) return;
        this.lastMapViewportSignature = signature;

        if (this.behaviorTracker) {
          this.behaviorTracker.trackMapInteraction(interactionType, {
            zoom: Number.isFinite(Number(zoom)) ? Number(zoom) : undefined,
            center_lat_bucket: centerLat,
            center_lng_bucket: centerLng
          });
        }
      } catch {}
    }

    trackPropertyPreview(data) {
      const currentId = String(data?.uniqueId || data?.attributes?.CATASTRO || "");
      if (!currentId || this.lastTrackedMarkerId === currentId) return;
      this.lastTrackedMarkerId = currentId;

      const saleAmount = Number(data?.attributes?.SALESAMT);
      let priceBand = "unknown";
      if (Number.isFinite(saleAmount)) {
        if (saleAmount < 100000) priceBand = "under_100k";
        else if (saleAmount < 250000) priceBand = "100k_249k";
        else if (saleAmount < 500000) priceBand = "250k_499k";
        else priceBand = "500k_plus";
      }

      trackEvent("property_preview", {
        municipality: data?.attributes?.MUNICIPIO || "",
        transaction_type: this.translateTransaction(transactionType(data?.attributes || {})) || "",
        price_band: priceBand
      });
    }

    reportFrontendError(type, errorLike, options = {}) {
      trackFrontendError({
        type,
        source: options.source || "app",
        message: errorToMessage(errorLike),
        route: this.getCurrentRouteName(),
        sourcePath: options.sourcePath || "",
        endpoint: options.endpoint || "",
        status: options.status,
        line: options.line,
        column: options.column,
        extra: {
          page_index: this.state.pageIndex || "",
          logged_in: Boolean(this.state.loggedIn),
          ...(options.extra || {})
        }
      });
      // Also record as a proper PostHog exception (grouped issue + stack trace).
      // The original error object carries the stack; before_send scrubs PII.
      posthogCaptureException(errorLike, {
        error_type: type,
        error_source: options.source || "app",
        route: this.getCurrentRouteName(),
      });
    }
    
    routeToFrontpagePrices = (reason = "upgrade", src = "") => {
      // Clear any token (only really matters if expired trial was still stored)
      try {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
      } catch {}
      clearAnalyticsUser();
    
      // Show Frontpage (logged out) + force prices route
      this.setState({
        loggedIn: false,
        token: "",
        decodeToken: "",
        loginBtnContainerShow: "loginBtnContainer",
        pageIndex: "",
    
        frontpageTarget: "prices",
        frontpageReason: reason || "",
        frontpageSrc: src || "",
      });
    
      // Optional tracking
      try { trackCustom("ViewPricing", { reason: reason || "", src: src || "" }); } catch {}
      trackEvent("view_pricing", {
        reason: reason || "upgrade",
        source: src || "app_route"
      });
      try { sendAnalytics("PRICES", `AUTO_ROUTE_${String(reason || "upgrade").toUpperCase()}`, String(new Date())); } catch {}
    };
    

    processLastVisit(){
      if((((Number(Date.now().toString()))-Number(localStorage.getItem('id')))>84600)&&(localStorage.getItem('id')!==null)){
        localStorage.setItem('id',Date.now().toString())
        sendAnalytics("HOMEPAGE","PAGEVISIT",String(new Date()))
      }
      else if(localStorage.getItem('id')===null){
        localStorage.setItem('id',Date.now().toString())
        sendAnalytics("HOMEPAGE","PAGEVISIT",String(new Date()))
      }
    }

	    // Add mousemove event listener on component mount
	    componentDidMount(){
	            this.installFuturisticPressFeedback();
            const loadFingerprint = () => {
              import('@fingerprintjs/fingerprintjs')
                .then((module) => (module.default || module).load())
                .then((fp) => fp.get())
                .then((result) => {
                  const visitorId = String(result?.visitorId || '').trim();
                  if (visitorId) {
                    this.setState({ fingerprint: visitorId });
                  }
                })
                .catch(() => {});
            };
            const scheduleFingerprint = () => {
              if (typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(loadFingerprint, { timeout: 5000 });
              } else {
                window.setTimeout(loadFingerprint, 2000);
              }
            };
            if (window.innerWidth < 780) {
              window.setTimeout(scheduleFingerprint, 9000);
            } else {
              scheduleFingerprint();
            }
            let emailVerificationToken = '';
            // 0) Trial token ingestion (COOKIE-first; querystring fallback). Always clean URL.
            try {
              const params = new URLSearchParams(window.location.search);
              // ? NEW: Landing param from backend: ?go=prices&reason=...&src=...
              const go = String(params.get("go") || "").toLowerCase();
              if (go === "prices" || go === "pricing") {
                const reason = params.get("reason") || "";
                const src = params.get("src") || "";
                this.setState({ frontpageTarget: "prices", frontpageReason: reason, frontpageSrc: src });
              }
              // ?go=stats: logged-in users get the in-app statistics overlay
              // (it only renders inside the map view); anonymous visitors go
              // to the public indexable statistics page instead.
              if (go === "stats") {
                const hasStoredToken = Boolean(
                  localStorage.getItem("token") || sessionStorage.getItem("token")
                );
                if (hasStoredToken) {
                  this.setState({ pageIndex: "stats" });
                } else {
                  window.location.replace("/es/estadisticas-puerto-rico");
                }
              }
              emailVerificationToken = String(params.get("verifyEmail") || "").trim();

              // Fallback (old flow)
              const qsTrialToken = params.get("trialToken");
              const qsFromTrial  = params.get("fromTrial");
      
              // New flow (cookie mode)
              const cookieTrialToken = this.getCookie("cd_trial_token");
              const cookieFromTrial  = this.getCookie("cd_trial_from"); // optional flag cookie
      
              const trialToken = cookieTrialToken || qsTrialToken;
              const fromTrial  = cookieFromTrial || qsFromTrial;
      
              // If trial was activated, show trial tutorial prompt
              if (trialToken || fromTrial === "1") {
                this.setState({ trialTutorialFlag: true });
              }
      
              if (trialToken) {
            localStorage.setItem("token", trialToken);

            // fire StartTrial once per token (prevents double firing on refresh)
	            const key = "cd_evt_start_trial_" + String(trialToken).slice(0, 16);
	            if (!localStorage.getItem(key)) {
	              localStorage.setItem(key, "1");
	              const trialEventValue = 0;
	              trackCustom('StartTrial', {
	                trial: 'PlusMAX_6h',
	                value: trialEventValue,
	                currency: 'USD'
	              });
	              track('StartTrial', {
	                plan: 'PlusMAX',
	                trial_name: 'PlusMAX_6h',
	                value: trialEventValue,
	                currency: 'USD'
	              });
	              trackEvent("start_trial", {
	                plan_tier: "PlusMAX",
	                trial_name: "PlusMAX_6h",
	                source: "landing_token"
	              });

	              // Reset activation counters for this trial token (keyed by username when possible).
	              let trialKey = String(trialToken).slice(0, 16);
	              try {
	                const decoded = jwt_decode(trialToken);
	                const uname = String(decoded?.username || "").trim();
	                if (uname) trialKey = uname;
	              } catch {}
	              try { localStorage.setItem(`cd_trial_search_count_${trialKey}`, "0"); } catch {}
	              try { localStorage.removeItem(`cd_evt_activated_trial_6_${trialKey}`); } catch {}
	              try { sessionStorage.removeItem(`cd_evt_activated_trial_6_${trialKey}`); } catch {}
	            }
	          }

      
              // Clear cookies so they can’t be reused
              if (cookieTrialToken) this.clearCookie("cd_trial_token");
              if (cookieFromTrial)  this.clearCookie("cd_trial_from");
      
              // Remove internal trial/router params, but preserve attribution params such as fbclid/utm_*
              if (
                window.location.search &&
                (
                  trialToken ||
                  params.has("trialToken") ||
                  params.has("fromTrial") ||
                  params.has("verifyEmail") ||
                  params.has("go") ||
                  params.has("reason") ||
                  params.has("src")
                )
              ) {
                const cleanUrl = this.buildCleanLandingUrl(params);
                window.history.replaceState({}, "", cleanUrl);
              }
              
            } catch (e) {
              this.reportFrontendError("trial_token_processing_error", e, {
                source: "app_mount",
                extra: { phase: "trial_token_bootstrap" }
              });
            }
      
    
      // 1) Existing logic
      try {
        document
          .querySelector("#root > div > div > div.leaflet-control-container > div.leaflet-bottom.leaflet-right")
          .style = "display:None;";
      } catch {}
    
      // 2) Now verifyToken will see the trial token if present
      if (emailVerificationToken) {
        this.consumeEmailVerificationToken(emailVerificationToken);
      } else {
        this.verifyToken();

        const activeToken = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (activeToken) {
          try { this.fetchTutorialPrompt() } catch {}
          try { this.fetchTrials() } catch {}
          try { this.decodeToken() } catch {}
        } else {
          this.syncAnalyticsUser(null);
        }
      }

      this.trackActiveView();

      // Trial expiry watcher: keeps the countdown chip fresh and, the moment
      // the trial JWT lapses, routes to prices instead of letting the next
      // API call surface a 401.
      this.trialExpiryTimer = setInterval(() => {
        try {
          const decoded = this.state.decodeToken;
          if (!decoded || !this.isTrialSession(decoded)) return;
          const expMs = Number(decoded.exp || 0) * 1000;
          if (!Number.isFinite(expMs) || expMs <= 0) return;
          if (Date.now() >= expMs) {
            this.routeToFrontpagePrices("trial_expired", "trial_countdown");
          } else if (this.state.loggedIn === true) {
            this.setState({ trialCountdownTick: Date.now() });
          }
        } catch {}
      }, 30000);

      this.behaviorTracker = startBehaviorTracking({
        getContext: () => ({
          route: this.getCurrentRouteName(),
          logged_in: Boolean(this.state.loggedIn),
          plan_tier: this.getPlanTierFromDecoded(this.state.decodeToken),
          language: this.state.selectedLanguage,
          results_count: Array.isArray(this.state.dataRegister) ? this.state.dataRegister.length : 0
        })
      });
      this.trackAppRouteAndFunnel("app_mount");
      trackEvent("app_initialized", {
        language: this.state.selectedLanguage,
        logged_in: Boolean(this.state.loggedIn)
      });
    }
    
    
    isMarketingTrialTokenNow() {
      try {
        const t = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
        if (!t) return { isTrial: false, key: ''};

        const d = jwt_decode(t);
        const uname = String(d.username || '');
        const isTrial = uname.startsWith('trial-') || String(d.initial || '').toUpperCase() === 'TEMPORARY';

        // use username for stable key
        return { isTrial, key: uname || t.slice(0, 16) };
      } catch (e) {
        return { isTrial: false, key: ''};
      }
    }

	    bumpActivatedTrialSearch() {
	      const { isTrial, key } = this.isMarketingTrialTokenNow();
	      if (!isTrial || !key) return;

	      const countKey = `cd_trial_search_count_${key}`;
	      const firedKey = `cd_evt_activated_trial_6_${key}`;

	      let n = 0;
	      try { n = parseInt(localStorage.getItem(countKey) || '0', 10) || 0; } catch (e) {}
	      if (!n) {
	        try { n = parseInt(sessionStorage.getItem(countKey) || '0', 10) || 0; } catch (e) {}
	      }
	      n += 1;
	      try { localStorage.setItem(countKey, String(n)); } catch (e) {}
	      try { sessionStorage.setItem(countKey, String(n)); } catch (e) {}

	      if (n < 6) return;

	      // One-time firing per trial user.
	      try {
	        if (!this.activatedTrial6FiredKeys) this.activatedTrial6FiredKeys = new Set();
	      } catch {}

	      let alreadyFired = false;
	      try { if (this.activatedTrial6FiredKeys && this.activatedTrial6FiredKeys.has(key)) alreadyFired = true; } catch {}
	      if (!alreadyFired) {
	        try { if (localStorage.getItem(firedKey)) alreadyFired = true; } catch (e) {}
	      }
	      if (!alreadyFired) {
	        try { if (sessionStorage.getItem(firedKey)) alreadyFired = true; } catch (e) {}
	      }
	      if (alreadyFired) return;

	      // Mark fired before tracking (prevents double-fire even if tracking throws).
	      try { if (this.activatedTrial6FiredKeys) this.activatedTrial6FiredKeys.add(key); } catch {}
	      try { localStorage.setItem(firedKey, '1'); } catch (e) {}
	      try { sessionStorage.setItem(firedKey, '1'); } catch (e) {}

	      try { trackCustom('ActivatedTrial_6Searches', { trial: 'PlusMAX_6h', searches: n }); } catch {}
	      try {
	        trackEvent("activated_trial", {
	          plan_tier: "PlusMAX",
	          trigger: "six_searches",
	          searches: n
	        });
	      } catch {}
	}

 

    decodeToken(){
      // Retrieve the token from localStorage
  
      let token;
  
      token = localStorage.getItem('token');
      
      if(!token){
        token = sessionStorage.getItem('token');
      }
  
      if (token) {
        // Decode the token
        const decodedToken = jwt_decode(token);

        if(this.state.paidTierAtInit===true && !hasPlusMaxAccess(decodedToken)){
          setTimeout(()=>this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'}),1000)
        }

        // Check if the token has expired
  
        // Update state with the token and its expiration status
        this.setState({ decodeToken:decodedToken });
        this.syncAnalyticsUser(decodedToken);
      } else {
        this.syncAnalyticsUser(null);
    }
      
    }

    getComparableSaleAmount(row) {
      const saleAmt = Number(row?.attributes?.SALESAMT);
      return Number.isFinite(saleAmt) ? saleAmt : NaN;
    }

    buildComparableRows(features = []) {
      const rows = Array.isArray(features) ? features : [];
      const batchId = Date.now();
      let serial = 0;

      return rows.reduce((accumulator, feature, index) => {
        const saleAmt = this.getComparableSaleAmount(feature);
        if (!Number.isFinite(saleAmt) || saleAmt < 0) return accumulator;

        accumulator.push({
          ...feature,
          uniqueId: feature?.uniqueId || `uid_${batchId}_${index}_${serial++}`
        });
        return accumulator;
      }, []);
    }

    doesComparableRowMatchDeletion = (row, target = {}) => {
      const rowUniqueId = String(row?.uniqueId || "").trim();
      const targetUniqueId = String(target?.uniqueId || "").trim();

      if (rowUniqueId && targetUniqueId) {
        return rowUniqueId === targetUniqueId;
      }

      const attributes = row?.attributes || {};
      const rowSaleAmt = Number(attributes.SALESAMT);
      const targetSaleAmt = Number(target?.saleAmt);
      const rowSalesDateRaw = Math.abs(Number(attributes.SALESDTTM));
      const targetSalesDateRaw = Math.abs(Number(target?.salesDateRaw));
      const rowCatastro = String(attributes.CATASTRO || "").trim();
      const targetCatastro = String(target?.catastro || "").trim();
      const rowAddress = String(attributes.DIRECCION_FISICA || "").trim();
      const targetAddress = String(target?.address || "").trim();

      const saleMatches = Number.isFinite(rowSaleAmt) && Number.isFinite(targetSaleAmt) && rowSaleAmt === targetSaleAmt;
      const dateMatches = Number.isFinite(rowSalesDateRaw) && Number.isFinite(targetSalesDateRaw) && rowSalesDateRaw === targetSalesDateRaw;
      const catastroMatches = !targetCatastro || (rowCatastro && rowCatastro === targetCatastro);
      const addressMatches = !targetAddress || (rowAddress && rowAddress === targetAddress);

      return saleMatches && dateMatches && catastroMatches && addressMatches;
    }

    handleDeleteComparablePoint = (target = {}) => {
      const targetUniqueId = String(target?.uniqueId || "").trim();
      const targetSaleAmt = Number(target?.saleAmt);
      const targetSalesDateRaw = Math.abs(Number(target?.salesDateRaw));
      const hasDeleteIdentity = Boolean(targetUniqueId)
        || (Number.isFinite(targetSaleAmt) && Number.isFinite(targetSalesDateRaw));

      if (!hasDeleteIdentity) return;

      this.setState((prev) => {
        const removeMatchingRows = (rows) => {
          if (!Array.isArray(rows)) return [];
          return rows.filter((row) => !this.doesComparableRowMatchDeletion(row, target));
        };

        return {
          dataRegister: removeMatchingRows(prev.dataRegister),
          masterDataRegister: removeMatchingRows(prev.masterDataRegister),
          filteredDataRegister: removeMatchingRows(prev.filteredDataRegister)
        };
      }, () => {
        if (targetUniqueId) {
          delete this.markerRefs[targetUniqueId];
        }
      });
    }

    getRowsScopedForOutlierAnalysis(rows = this.state.dataRegister) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const minStruct = this.state.minStruct === "" ? Number.NEGATIVE_INFINITY : Number(this.state.minStruct);
      const maxStruct = this.state.maxStruct === "" ? Number.POSITIVE_INFINITY : Number(this.state.maxStruct);
      const startDate = this.state.startDate === "" ? Number.NEGATIVE_INFINITY : Number(this.state.startDate);
      const endDate = this.state.endDate === "" ? Number.POSITIVE_INFINITY : Number(this.state.endDate);

      return sourceRows.filter((row) => {
        const attributes = row?.attributes || {};
        const rawStructure = Number(attributes.STRUCTURE);
        const indexedDate = formatDateYYYYMMDD(attributes.SALESDTTM);
        const formattedDate = indexedDate ? new Date(indexedDate) : new Date(NaN);

        if (!Number.isFinite(rawStructure)) return false;
        if (Number.isNaN(formattedDate.getTime())) return false;

        return rawStructure >= minStruct
          && rawStructure <= maxStruct
          && formattedDate.getTime() >= startDate
          && formattedDate.getTime() <= endDate;
      });
    }

    computeInterpolatedQuantile(sortedValues = [], percentile = 0.5) {
      if (!Array.isArray(sortedValues) || sortedValues.length === 0) return NaN;

      const boundedPercentile = Math.min(1, Math.max(0, Number(percentile) || 0));
      const maxIndex = sortedValues.length - 1;
      const scaledIndex = maxIndex * boundedPercentile;
      const lowerIndex = Math.floor(scaledIndex);
      const upperIndex = Math.ceil(scaledIndex);

      if (lowerIndex === upperIndex) {
        return sortedValues[lowerIndex];
      }

      const weight = scaledIndex - lowerIndex;
      return sortedValues[lowerIndex] + ((sortedValues[upperIndex] - sortedValues[lowerIndex]) * weight);
    }

    getSaleOutlierSummary(rows = this.state.dataRegister) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const {
        lowerQuantile,
        upperQuantile,
        lowerIqrMultiplier,
        upperIqrMultiplier,
        minSampleSize
      } = SALE_OUTLIER_FILTER_SETTINGS;
      const analysisRows = this.getRowsScopedForOutlierAnalysis(sourceRows);
      const cacheKey = [
        Boolean(this.state.filterOutliers),
        lowerQuantile,
        upperQuantile,
        lowerIqrMultiplier,
        upperIqrMultiplier,
        minSampleSize,
        this.state.startDate === "" ? "none" : Number(this.state.startDate),
        this.state.endDate === "" ? "none" : Number(this.state.endDate),
        this.state.minStruct === "" ? "none" : Number(this.state.minStruct),
        this.state.maxStruct === "" ? "none" : Number(this.state.maxStruct)
      ].join("|");

      if (
        this.outlierFilterCache.rowsRef === sourceRows &&
        this.outlierFilterCache.cacheKey === cacheKey &&
        this.outlierFilterCache.summary
      ) {
        return this.outlierFilterCache.summary;
      }

      const saleValues = analysisRows
        .map((row) => this.getComparableSaleAmount(row))
        .filter((value) => Number.isFinite(value) && value >= 0)
        .sort((a, b) => a - b);

      const baseSummary = {
        enabled: Boolean(this.state.filterOutliers),
        applied: false,
        reason: "disabled",
        lowerMultiplier: lowerIqrMultiplier,
        upperMultiplier: upperIqrMultiplier,
        totalCount: analysisRows.length,
        validCount: saleValues.length,
        keptCount: analysisRows.length,
        removedCount: 0,
        q1: null,
        q3: null,
        iqr: null,
        lowerBound: null,
        upperBound: null,
        filteredRows: analysisRows
      };

      let summary = baseSummary;

      if (baseSummary.enabled !== true) {
        summary = { ...baseSummary, reason: "disabled" };
      } else if (saleValues.length < minSampleSize) {
        summary = {
          ...baseSummary,
          reason: "insufficient_sample"
        };
      } else {
        const q1 = this.computeInterpolatedQuantile(saleValues, lowerQuantile);
        const q3 = this.computeInterpolatedQuantile(saleValues, upperQuantile);
        const iqr = q3 - q1;

        if (!Number.isFinite(iqr) || iqr <= 0) {
          summary = {
            ...baseSummary,
            reason: "flat_distribution",
            q1,
            q3,
            iqr
          };
        } else {
          const lowerBound = Math.max(0, q1 - (iqr * lowerIqrMultiplier));
          const upperBound = q3 + (iqr * upperIqrMultiplier);
          const filteredRows = analysisRows.filter((row) => {
            const saleAmt = this.getComparableSaleAmount(row);
            return Number.isFinite(saleAmt) && saleAmt >= lowerBound && saleAmt <= upperBound;
          });

          summary = {
            ...baseSummary,
            applied: true,
            reason: "applied",
            q1,
            q3,
            iqr,
            lowerBound,
            upperBound,
            keptCount: filteredRows.length,
            removedCount: Math.max(0, analysisRows.length - filteredRows.length),
            filteredRows
          };
        }
      }

      this.outlierFilterCache = {
        rowsRef: sourceRows,
        cacheKey,
        summary
      };

      return summary;
    }

    applyOutlierFilter(rows = this.state.dataRegister) {
      const summary = this.getSaleOutlierSummary(rows);
      return Array.isArray(summary.filteredRows) ? summary.filteredRows : (Array.isArray(rows) ? rows : []);
    }

    applyQuickSearchFilters(rows = []) {
      const minPrice = this.state.minPrice === "" ? Number.NEGATIVE_INFINITY : Number(this.state.minPrice);
      const maxPrice = this.state.maxPrice === "" ? Number.POSITIVE_INFINITY : Number(this.state.maxPrice);

      return this.applyOutlierFilter(rows).filter((row) => {
        const saleAmt = this.getComparableSaleAmount(row);
        return Number.isFinite(saleAmt) && saleAmt >= minPrice && saleAmt <= maxPrice;
      });
    }

    getSalesColorScaleMax(rows = this.state.dataRegister) {
      const salesPriceRegister = (Array.isArray(rows) ? rows : [])
        .map((x) => Number(x?.attributes?.SALESAMT))
        .filter((value) => Number.isFinite(value) && value >= 0);

      const dataMax = salesPriceRegister.length > 0 ? Math.max(...salesPriceRegister) : 0;
      const maxPriceCap = Number(this.state.maxPrice);
      const cappedMax = Number.isFinite(maxPriceCap) && maxPriceCap > 0
        ? Math.min(dataMax || maxPriceCap, maxPriceCap)
        : (dataMax > 0 ? dataMax : 1);

      return cappedMax > 0 ? cappedMax : 1;
    }

    // Define method to calculate and return color based on sales amount
    getSalesAmtColor(salesAmt, safeMaxOverride = null) {
      const safeMax = (Number.isFinite(Number(safeMaxOverride)) && Number(safeMaxOverride) > 0)
        ? Number(safeMaxOverride)
        : this.getSalesColorScaleMax();
      const normalized = Number.isFinite(salesAmt) ? (salesAmt / safeMax) : 0;
      const ratio = Math.pow(Math.max(0, Math.min(1, normalized)), 0.92);

      const stops = [
        { t: 0, c: [34, 197, 94] },
        { t: 0.35, c: [132, 204, 22] },
        { t: 0.62, c: [250, 204, 21] },
        { t: 0.82, c: [249, 115, 22] },
        { t: 1, c: [220, 38, 38] }
      ];

      const upperIndex = stops.findIndex((stop) => ratio <= stop.t);
      if (upperIndex === -1) {
        const last = stops[stops.length - 1].c;
        return `rgb(${last[0]}, ${last[1]}, ${last[2]})`;
      }
      if (upperIndex === 0) {
        const first = stops[0].c;
        return `rgb(${first[0]}, ${first[1]}, ${first[2]})`;
      }

      const lower = stops[upperIndex - 1];
      const upper = stops[upperIndex];
      const segmentRatio = (ratio - lower.t) / (upper.t - lower.t);
      const mixed = lower.c.map((channel, i) => Math.round(channel + ((upper.c[i] - channel) * segmentRatio)));

      return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
    }
  
	  componentWillUnmount() {
	      this.removeFuturisticPressFeedback();
	      try {
	        if (this.gpsMarkerTimer) {
	          clearTimeout(this.gpsMarkerTimer);
          this.gpsMarkerTimer = null;
        }
      } catch {}
      try {
        if (this.gpsErrorTimer) {
          clearTimeout(this.gpsErrorTimer);
          this.gpsErrorTimer = null;
        }
      } catch {}
      try {
        if (this.behaviorTracker && typeof this.behaviorTracker.stop === "function") {
          this.behaviorTracker.stop();
        }
      } catch {}
      try {
        if (this.trialExpiryTimer) {
          clearInterval(this.trialExpiryTimer);
          this.trialExpiryTimer = null;
        }
      } catch {}
    }

  componentDidCatch(error, info) {
      this.reportFrontendError("react_component_error", error, {
        source: "react_component_tree",
        extra: {
          phase: "component_did_catch",
          component_stack: info?.componentStack || ""
        }
      });
      this.setState({ errorActive: "application" });
    }

  componentDidUpdate(prevProps, prevState){
    if (this.state.loggedIn === true) {
      this.getMinMaxDateRange();
      this.upDateBlankBoxes();
      try {
        document.querySelector("#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control > div:nth-child(2) > div > a.leaflet-draw-edit-remove.leaflet-disabled").title = "Borrar Seleccion";
      } catch {}
      this.toggleTools();
    }

    if (
      prevState.pageIndex !== this.state.pageIndex ||
      prevState.loggedIn !== this.state.loggedIn
    ) {
      this.trackActiveView();
      this.trackAppRouteAndFunnel("route_or_auth_change");
    }

    if (prevState.selectedLanguage !== this.state.selectedLanguage && this.state.decodeToken) {
      this.syncAnalyticsUser(this.state.decodeToken);
    }

    if (prevState.selectedLanguage !== this.state.selectedLanguage) {
      trackEvent("language_context_updated", {
        language: this.state.selectedLanguage
      });
    }

    if (!prevState.loggedIn && this.state.loggedIn && this.behaviorTracker) {
      this.behaviorTracker.trackFunnelStage("login_completed");
    }

    // Non-blocking Area Pulse fetch once a Pro session is established.
    if (this.state.loggedIn === true && this.state.decodeToken && this.pulseFetchedForSession === false) {
      try { this.maybeFetchAreaPulse(); } catch {}
    }

    if (prevState.loggedIn && !this.state.loggedIn && this.behaviorTracker) {
      this.behaviorTracker.trackCustomAction("db_logout_transition", {
        from_route: this.getCurrentRouteName()
      });
    }

    const wasVerificationPending = Boolean(prevState.decodeToken && prevState.decodeToken.emailVerified === false);
    const isVerificationPending = Boolean(this.state.decodeToken && this.state.decodeToken.emailVerified === false);
    if (wasVerificationPending !== isVerificationPending && isVerificationPending === true) {
      this.setState({ authNoticeExpanded: true });
    }

    const prevResults = Array.isArray(prevState.dataRegister) ? prevState.dataRegister.length : 0;
    const nextResults = Array.isArray(this.state.dataRegister) ? this.state.dataRegister.length : 0;
    if (prevResults === 0 && nextResults > 0 && this.behaviorTracker) {
      this.behaviorTracker.trackFunnelStage("first_results_observed", {
        result_count: nextResults
      });
    }
    if (prevResults !== nextResults) {
      trackEvent("results_count_changed", {
        results_count: nextResults
      });
    }

    this.trackFilterAdjustments(prevState);
    }

    acceptFreshAuthToken(authToken) {
      const token = String(authToken || '').trim();
      if (!token) {
        this.verifyToken();
        return;
      }

      try {
        const decoded = jwt_decode(token);
        const expiresAt = Number(decoded?.exp || 0) * 1000;
        if (expiresAt && Date.now() >= expiresAt) {
          throw new Error('Fresh authentication token is expired.');
        }

        this.setState({
          token,
          decodeToken: decoded,
          loginBtnContainerShow: 'loginBtnContainerHide',
          pageIndex: '',
          loggedIn: true,
          authNoticeMessage: '',
          authNoticeTone: 'info'
        });
        this.syncAnalyticsUser(decoded);

        if (this.state.paidTierAtInit === true && !hasPlusMaxAccess(decoded)) {
          window.setTimeout(
            () => this.setState({ pageIndex: 'account', subscriptionIndex: 'PlusMAX' }),
            1000
          );
        }

        trackEvent('session_validated', { source: 'fresh_auth_response' });
      } catch (error) {
        this.reportFrontendError('fresh_auth_token_decode_failed', error, {
          source: 'login_response'
        });
        this.verifyToken();
      }
    }

    userIsLogedIn(authToken = ''){
      if (authToken) {
        this.acceptFreshAuthToken(authToken);
        return;
      }
      this.verifyToken();
    }

    verifyToken() {
      // Retrieve the token from localStorage
      let token = localStorage.getItem('token');
      this.setState({ token: token });
    
      // Check if the token is not found
      if (!token) {
        token = sessionStorage.getItem('token');
        this.setState({ token: token });
      }
    
      if (!token) {
        this.setState({token: ''});
        this.syncAnalyticsUser(null);
        return;
      }
    
      this.decodeToken();
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        VERIFY_TOKEN_TIMEOUT_MS
      );
      const startedOnLoginPage = this.state.pageIndex === 'login';

      fetch(`${this.state.serverIp}/api/verify-token`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          // Include the token in the Authorization header
          'Authorization': `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (!res.ok) {
            // detect trial vs normal before clearing
            let wasTrial = false;
            let wasPass = false;
            let wasPendingVerification = false;
            try {
              const d = jwt_decode(token);
              wasTrial = this.isTrialDecoded(d);
              wasPass = this.isPassDecoded(d);
              wasPendingVerification = Boolean(d?.emailVerified === false);
            } catch {}
          
            localStorage.removeItem("token");
            sessionStorage.removeItem("token");
            this.syncAnalyticsUser(null);
          
            if (wasTrial) {
              // Trial rejected by server (expired, invalid, etc) -> prices
              this.routeToFrontpagePrices("trial_expired", "verify_token");
            } else if (wasPass) {
              trackEvent("funnel_stage_reached", { stage: "pass_expired_viewed" });
              trackEvent("pass_expired_viewed", { source: "verify_token" });
              this.routeToFrontpagePrices("pass_expired", "verify_token");
            } else {
              // normal behavior
              this.setState({
                loggedIn: false,
                token: "",
                decodeToken: "",
                loginBtnContainerShow: "loginBtnContainer",
                authNoticeMessage: wasPendingVerification
                  ? this.languageIndexer(
                      'Tu acceso provisional expiro. Verifica tu email o pide un enlace nuevo para continuar.',
                      'Your provisional access expired. Verify your email or request a new link to continue.'
                    )
                  : '',
                authNoticeTone: wasPendingVerification ? 'error' : 'info'
              });
            }
          
            return res.text().then(text => Promise.reject(text));
          }
          
          // Return the promise containing the response data, which will be processed in the next .then
          return res.json();
        })
        .then(
          (data) => {
            if (data?.newToken) {
              try {
                if (localStorage.getItem('token')) {
                  localStorage.setItem('token', data.newToken);
                } else {
                  sessionStorage.setItem('token', data.newToken);
                }
                const refreshedDecoded = jwt_decode(data.newToken);
                this.setState({
                  token: data.newToken,
                  decodeToken: refreshedDecoded,
                  loginBtnContainerShow: "loginBtnContainerHide",
                  pageIndex: '',
                  loggedIn: true,
                });
                this.syncAnalyticsUser(refreshedDecoded);
              } catch {}
            } else {
              this.setState({
                loginBtnContainerShow: "loginBtnContainerHide",
                pageIndex: '',
                loggedIn: true,
              });
            }
            trackEvent("session_validated");
          }
        )
        .catch((error) => {
          // Network / transient errors should NOT wipe a valid trial token.
          // If the token is truly invalid, the server would respond with !res.ok above.
          console.warn("verifyToken() failed:", error);
          const transientFailure =
            error?.name === 'AbortError' || error instanceof TypeError;
          if (transientFailure) {
            const timedOut = error?.name === 'AbortError';
            this.setState((current) => ({
              pageIndex:
                startedOnLoginPage && current.pageIndex === 'login'
                  ? ''
                  : current.pageIndex,
              authNoticeMessage: timedOut
                ? this.languageIndexer(
                    'La verificacion de sesion tardo demasiado. Conservamos tu sesion; intenta nuevamente.',
                    'Session verification timed out. Your session was kept; please try again.'
                  )
                : this.languageIndexer(
                    'No pudimos verificar la sesion por un problema de red. Intenta nuevamente.',
                    'We could not verify the session because of a network problem. Please try again.'
                  ),
              authNoticeTone: 'error',
              authNoticeExpanded: true
            }));
          }
          this.reportFrontendError("verify_token_request_failed", error, {
            source: "verify_token",
            endpoint: "/api/verify-token"
          });
        })
        .finally(() => {
          window.clearTimeout(timeoutId);
        });
    }
    


  upDateBlankBoxes(){
    try{
      if(document.getElementById("minno").value===""&&this.state.minPrice!==0){
        document.getElementById("minno").value=this.state.minPrice
      }
      else if(document.getElementById("maxno").value===""&&this.state.maxPrice!==999999999){
        document.getElementById("maxno").value=this.state.maxPrice
      }
      else if(document.getElementById("minStruct").value===""&&this.state.minStruct!==0){
        document.getElementById("minStruct").value=this.state.minStruct/10
      }
      else if(document.getElementById("maxStruct").value===""&&this.state.maxStruct!==999999999){
        document.getElementById("maxStruct").value=this.state.maxStruct/10
      }
    }catch{}
  }

  formatDireccion(address){
    try{
      if(address===null || address===undefined){return address;}
      let cleanAddress = String(address).trim();

      // Remove trailing location noise like:
      // "pr puerto rico 00000", "PR PUERTO RICO 00926", etc.
      cleanAddress = cleanAddress.replace(
        /\s*,?\s*pr\s+puerto\s+rico(?:\s+\d{5}(?:-\d{4})?)?\s*$/i,
        ""
      );

      // Optional trailing PR token (only if it's the suffix)
      cleanAddress = cleanAddress.replace(/\s*,?\s*pr\s*$/i, "");

      return cleanAddress.replace(/\s{2,}/g, " ").trim();
    }
    catch{return address}
  }

getMinMaxDateRange() {
    if(this.state.dataRegister.length>0&&this.state.startDate===""){
      let dateRange = this.state.dataRegister
        .map((data) => Number(data?.attributes?.SALESDTTM))
        .filter((value) => Number.isFinite(value));
      if (dateRange.length === 0) return;
      const minDate = Math.abs(Math.min.apply(null, dateRange));
      const maxDate = Math.abs(Math.max.apply(null, dateRange));
      this.setState({
        startDate: toDateMs(minDate),
        endDate: toDateMs(maxDate),
        startDateToDisplay: toDateObject(minDate),
        endDateToDisplay: toDateObject(maxDate)
      });
    }
}

getMinMaxDateRangeRepo() {
  if(this.state.repoData.length>0&&this.state.startDateRepo===""){
    let dateRange = this.state.repoData
      .map((data) => Number(data?.attributes?.SALESDTTM))
      .filter((value) => Number.isFinite(value));
    if (dateRange.length === 0) return;
    const minDate = Math.abs(Math.min.apply(null, dateRange));
    const maxDate = Math.abs(Math.max.apply(null, dateRange));
    this.setState({
      startDateRepo: toDateMs(minDate),
      endDateRepo: toDateMs(maxDate),
      startDateToDisplayRepo: toDateObject(minDate),
      endDateToDisplayRepo: toDateObject(maxDate)
    });
  }
}
  
handleInformaticError(){
  if(this.state.errorActive==='application')
    return(
      <section className='errorPage'>
        <div className='errorPageInner'>
          <div>Ocurrio un problema al mostrar esta pantalla.</div>
          <div>Presiona Volver para continuar.</div>
          <button onClick={()=>this.setState({errorActive:''})}>Volver</button>
        </div>
      </section>
    )
  if(this.state.errorActive==='active')
    return(
      <section className='errorPage'>
        <div className='errorPageInner'>
          <div>Hay problemas con la conexion al servidor, trata lo siguiente para posibles soluciones:</div>
          <div>-Verifica tu conexion al internet.</div>
          <div>-Reinicia tu dispositivo.</div>
          <div>-Trate de conectarse mas tarde.</div>
          <button onClick={()=>this.setState({errorActive:''})}>Volver</button>
        </div>
      </section>
    )
}

    // Keep map toolbar chrome in sync without doing work on every mousemove.
handleClick = () => {
  this.toggleTools();
  try {
    document
      .querySelector(
        "#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control > div:nth-child(2) > div > a.leaflet-draw-edit-edit.leaflet-disabled"
      )
      .style["display"] = "None";
  } catch {}
};


    _onCreate = (e) => {
      this.decodeToken()
      // A freshly drawn polygon supersedes any saved-area view.
      this.removePulsePolygonLayer()
      const { layerType, layer } = e;
      if (layerType === 'polygon') {
        const coordinates = layer.getLatLngs();
        const vertices = Array.isArray(coordinates?.[0]) ? coordinates[0].length : 0;
        const transformed = {
          "rings": coordinates.map((ring) =>
            ring.map((coord) => [coord.lng,coord.lat])
          )
        };
        this.setState({
          setCoords:JSON.stringify(transformed['rings']),
          toggleBg:'toggleInactive',
        })
        trackEvent("polygon_drawn", {
          vertices,
          source: "map_draw_tool"
        });
      }
      document.querySelector("#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control > div:nth-child(1)").style="display:None;";
    }

    sendRequest() {
      this.setState({ fetchingStatus: "fetching" });
      trackSearchInteraction({
        searchType: "map_polygon",
        isTrialSearch: false
      });
      trackEvent("map_search_submitted", {
        search_mode: "standard",
        filter_outliers: this.state.filterOutliers,
        outlier_iqr_multiplier: SALE_OUTLIER_FILTER_SETTINGS.iqrMultiplier
      });
      track('Search', {
        search_string: 'map_polygon',
        content_category: 'property',
        search_mode: 'standard'
      });
      if (!this.firstSearchTracked) {
        this.firstSearchTracked = true;
        trackEvent("time_to_first_search", {
          search_mode: "standard",
          seconds_from_session_start: Math.max(1, Math.round((Date.now() - this.sessionStartedAt) / 1000))
        });
      }
      if (this.behaviorTracker) {
        this.behaviorTracker.trackFunnelStage("search_started");
        this.behaviorTracker.trackCustomAction("db_search_started", {
          search_mode: "standard"
        });
      }
      const token = this.state.token;
      let url = `${this.state.serverIp}/api/getHousingPoints`;
    
      if (window.location.protocol !== "file:") {
        fetch(url, {
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Referrer: "https://casadata.io/",
            Authorization: `Bearer ${token}`
          },
          redirect: "follow",
          body: JSON.stringify(this.state.setCoords)
        })
          .then((res) => res.json())
          .then((res) => {
            try {
              if (!Array.isArray(res?.features)) {
                throw new Error("invalid_search_response");
              }

              const rawRows = this.buildComparableRows(res.features);
              const previewRows = this.applyQuickSearchFilters(rawRows);

              // Keep the search polygon for the Pro save-area / report flows
              // (setCoords itself is cleared right after every search).
              let searchedPolygon = null;
              try { searchedPolygon = JSON.parse(this.state.setCoords); } catch {}

              this.setState((prev) => ({
                masterDataRegister: [...(prev.masterDataRegister || []), ...rawRows],
                dataRegister:       [...(prev.dataRegister       || []), ...rawRows],
                filteredDataRegister: [],
                llmFilterMode: false,
                setCoords: "",
                fetchingStatus: "",
                lastSearchPolygon: searchedPolygon || prev.lastSearchPolygon
              }));

              // Pro empty-state: one-time hint after the first search when the
              // user has zero saved areas (instead of the pulse modal).
              try {
                if (this.isProUser() && (this.state.savedAreas || []).length === 0 && !localStorage.getItem('cd_pro_save_hint_shown')) {
                  localStorage.setItem('cd_pro_save_hint_shown', '1');
                  this.setState({ showSaveAreaHint: true });
                  setTimeout(() => this.setState({ showSaveAreaHint: false }), 12000);
                }
              } catch {}
              trackSearchInteraction({
                searchType: "map_polygon",
                resultCount: previewRows.length,
                isTrialSearch: false
              });
              trackEvent("view_search_results", {
                search_mode: "standard",
                result_count: previewRows.length
              });
              if (this.behaviorTracker) {
                this.behaviorTracker.trackFunnelStage("search_results_returned", {
                  search_mode: "standard"
                });
                this.behaviorTracker.trackCustomAction("db_search_results", {
                  search_mode: "standard",
                  result_count: previewRows.length
                });
              }
              this.bumpActivatedTrialSearch();

              // Restore shape-draw toolbar
              const shapeBar = document.querySelector(
                "#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control > div:nth-child(1)"
              );
              if (shapeBar) {
                shapeBar.style = "";
              }
            } catch (err) {
              // Possibly handle upgrade prompt
              try {
                this.setState({ pageIndex: "upgradePromotion", remainingCoolDown: res?.cooldown, fetchingStatus: "" });
                trackEvent("search_limit_reached", {
                  search_mode: "standard"
                });
                if (this.behaviorTracker) {
                  this.behaviorTracker.trackFunnelStage("search_limit_hit");
                  this.behaviorTracker.trackCustomAction("db_search_limit", {
                    search_mode: "standard"
                  });
                }
              } catch {
                if (res?.ServerResponse && res.ServerResponse.includes("Token")) {
                  window.location.reload();
                } else {
                  this.setState({ fetchingStatus: "" });
                }
              }
            }
          })
          .catch((err) => {
            this.setState({ fetchingStatus: "", errorActive: "active", pageIndex: "" });
            trackEvent("map_search_failed", { search_mode: "standard" });
            if (this.behaviorTracker) {
              this.behaviorTracker.trackCustomAction("db_search_failed", {
                search_mode: "standard"
              });
            }
            this.reportFrontendError("map_search_failed", err, {
              source: "map_search",
              endpoint: "/api/getHousingPoints",
              extra: { search_mode: "standard" }
            });
          });
      }
    }
    

    turnOnPlusMaxMode(){
      this.setState(prevState => ({
        decodeToken: {
            ...prevState.decodeToken,
            delta: "11A5D7A0C" // Replace "newDeltaValue" with your desired value
        }
      }));
    }



    sendTrialRequest(){
      this.setState({fetchingStatus:"fetching"})
      trackSearchInteraction({
        searchType: "map_polygon",
        isTrialSearch: true
      });
      trackEvent("map_search_submitted", {
        search_mode: "trial",
        filter_outliers: this.state.filterOutliers,
        outlier_iqr_multiplier: SALE_OUTLIER_FILTER_SETTINGS.iqrMultiplier
      });
      track('Search', {
        search_string: 'map_polygon',
        content_category: 'property',
        search_mode: 'trial'
      });
      if (!this.firstSearchTracked) {
        this.firstSearchTracked = true;
        trackEvent("time_to_first_search", {
          search_mode: "trial",
          seconds_from_session_start: Math.max(1, Math.round((Date.now() - this.sessionStartedAt) / 1000))
        });
      }
      if (this.behaviorTracker) {
        this.behaviorTracker.trackFunnelStage("search_started");
        this.behaviorTracker.trackCustomAction("db_search_started", {
          search_mode: "trial"
        });
      }
      const token = this.state.token;      
      // this.setState({fetchingStatus:"fetching",maxPrice:9999999999,minPrice:0})
      let url = `${this.state.serverIp}/api/getTrialHousingPoints`
      if (window.location.protocol !== "file:"){
        fetch(url,{
          method: "POST", // *GET, POST, PUT, DELETE, etc.
          mode: "cors", // no-cors, *cors, same-origin
          cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
          credentials: "same-origin", // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control':'no-cache',
            'Referrer':'https://casadata.io/',
            // Include the token in the Authorization header
            'Authorization': `Bearer ${token}`,
          },
          redirect: "follow", // manual, *follow, error
          // referrerPolicy:"no-referrer-when-downgrade",
          body: JSON.stringify(this.state.setCoords), // body data type must match "Content-Type"
        })
        .then(res=>res.json())
        .then(res=>{
          try{
            if (!Array.isArray(res?.features)) {
              throw new Error("invalid_trial_search_response");
            }

            const rawRows = this.buildComparableRows(res.features);
            const previewRows = this.applyQuickSearchFilters(rawRows);

            this.setState((prevState) => ({
              masterDataRegister: [...(prevState.masterDataRegister || []), ...rawRows],
              dataRegister: [...(prevState.dataRegister || []), ...rawRows],
              setCoords:"",
              fetchingStatus:"",
              filteredDataRegister:[],
              llmFilterMode:false
            }));

            trackSearchInteraction({
              searchType: "map_polygon",
              resultCount: previewRows.length,
              isTrialSearch: true
            });
            trackEvent("view_search_results", {
              search_mode: "trial",
              result_count: previewRows.length
            });
	            if (this.behaviorTracker) {
	              this.behaviorTracker.trackFunnelStage("search_results_returned", {
	                search_mode: "trial"
	              });
	              this.behaviorTracker.trackCustomAction("db_search_results", {
	                search_mode: "trial",
	                result_count: previewRows.length
	              });
	            }
	            this.bumpActivatedTrialSearch();
              this.turnOnPlusMaxMode();
              this.fetchTrials();
              trackEvent("trial_search_completed");
              const shapeBar = document.querySelector("#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control > div:nth-child(1)");
              if (shapeBar) {
                shapeBar.style = "";
              }
	          }
	          catch{
	            try{
	              this.setState({pageIndex:'upgradePromotion',remainingCoolDown:res?.cooldown,fetchingStatus:""})
	              trackEvent("search_limit_reached", {
                search_mode: "trial"
              });
              if (this.behaviorTracker) {
                this.behaviorTracker.trackFunnelStage("search_limit_hit");
                this.behaviorTracker.trackCustomAction("db_search_limit", {
                  search_mode: "trial"
                });
              }

            }
            catch{
              if(res?.ServerResponse && res.ServerResponse.includes('Token')){
                window.location.reload();
              } else {
                this.setState({fetchingStatus:""});
              }
            }
          }
        })
        .catch((e)=>{
          this.setState({fetchingStatus:'',errorActive:'active',pageIndex:''})
          trackEvent("map_search_failed", { search_mode: "trial" });
          if (this.behaviorTracker) {
            this.behaviorTracker.trackCustomAction("db_search_failed", {
              search_mode: "trial"
            });
          }
          this.reportFrontendError("map_search_failed", e, {
            source: "map_search_trial",
            endpoint: "/api/getTrialHousingPoints",
            extra: { search_mode: "trial" }
          });
        })
      }      
    }

    searchBtnIndexer(){
      if(this.state.trials!==0){
        return(<button onClick={
          ()=>{
            this.sendTrialRequest()
            sendAnalytics("HOMEPAGE","PlusMAX Trial Search",String(new Date()))
          }
        }
        onFocus={
          ()=>{
            this.sendTrialRequest()
            sendAnalytics("HOMEPAGE","PlusMAX Trial Search",String(new Date()))
          }
        }
         className='searchBottom'>{this.languageIndexer("Busqueda Plus MAX",'PlusMAX Search')}</button>)
      }
      else{
        return(<div className='baseSearchBtn'>Buscar / Search</div>)
      }
    }

    togglePluxMaxTemp() {
      const doBaseSearch  = () => this.sendRequest();
      const doTrialSearch = () => { this.sendTrialRequest(); sendAnalytics("HOMEPAGE","PlusMAX Trial Search", String(new Date())); };
    
      if (this.state.decodeToken.delta !== '37485923' || this.state.trials === 0) {
        return (
          <button
            type="button"
            className="fetchData"
            onPointerUp={doBaseSearch}
            onClick={!window.PointerEvent ? doBaseSearch : undefined}
          >
            <div className="baseSearchBtn">Buscar / Search</div>
          </button>
        );
      }
    
      return (
        <div className="baseSearchField">
          <button
            type="button"
            className="searchBottom"
            onPointerUp={doTrialSearch}
            onClick={!window.PointerEvent ? doTrialSearch : undefined}
          >
            {this.languageIndexer("Busqueda Plus MAX","PlusMAX Search")}
          </button>
        </div>
      );
    }
    
    toggleSearchBtn() {
      if (this.state.setCoords !== "" && this.state.fetchingStatus === "") {
        return (
          <MapEventGuardLegacy as="div" className="sendBoxSmall leaflet-control" id="theSendBox">
            {this.togglePluxMaxTemp()}
            <label className="outliersCheckBox">
              <input
                checked={this.state.filterOutliers}
                onChange={() => this.setState((state) => ({ filterOutliers: !state.filterOutliers, llmFilterMode: false }))}
                type="checkbox"
              />
              Filtrar Outliers
            </label>
          </MapEventGuardLegacy>
        );
      }
    
      if (this.state.fetchingStatus === "fetching") {
        return (
          <MapEventGuardLegacy as="div" className="sendBoxSmall" id="theSendBox">
            <span className="mapSearchSpinner" role="status" aria-label="loading" />
            <div>Buscando...</div>
          </MapEventGuardLegacy>
        );
      }
    
      return null;
    }
    

    toggleTools = () => {
      if (this.state.zoomApropriate===true&&this.state.decodeToken!=="") {
        try{document.querySelector('#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control').style=""}catch{}
      }
      else{
        try{document.querySelector('#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control').style="display:none;"}catch{}
      }      
    }
    

    renderNumericLimits(){
      if(this.state.dataRegister.length>0 && this.state.sendBoxLimitsHide===false && this.checkIfUserIsPremium()){
        return(
          <div 
            onMouseDown={()=>this.holdTheMapStill()}
            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          className='sendBox sendBoxLimits groupedFilterBox' id="theSendBox">
            {this.getMinMaxDateRange()}
            <div className='groupedFilterGrid'>
              <input 
                type="number" 
                placeholder='Max Precio' 
                className='maxPrice' 
                id="maxno"
                onChange={()=>{
                  if(document.getElementById("maxno").value!==""){
                    this.setState({maxPrice:document.getElementById('maxno').value,llmFilterMode:false})
                  }
                  else{
                    this.setState({maxPrice:999999999,llmFilterMode:false})
                  }
                }}>
              </input>
      
              <input 
                type="number" 
                placeholder='Min Precio' 
                className='minPrice'
                id="minno"
                onChange={()=>{
                  if(document.getElementById("minno").value!==""){
                    this.setState({minPrice:document.getElementById('minno').value,llmFilterMode:false})
                  }
                  else{
                    this.setState({minPrice:0,llmFilterMode:false})
                  }
                }}>
              </input>

              <input 
                type="number" 
                placeholder='Max SQFT' 
                className='maxPrice'
                id="maxStruct"
                onChange={()=>{
                  if(document.getElementById("maxStruct").value!==""){
                    this.setState({maxStruct:document.getElementById('maxStruct').value*10,llmFilterMode:false})
                  }
                  else{
                    this.setState({maxStruct:999999999,llmFilterMode:false})
                  }
                }}>
              </input>

              <input 
                type="number" 
                placeholder='Min SQFT' 
                className='minPrice'
                id="minStruct"
                onChange={()=>{
                  if(document.getElementById("minStruct").value!==""){
                    this.setState({minStruct:document.getElementById('minStruct').value*10,llmFilterMode:false})
                  }
                  else{
                    this.setState({minStruct:0,llmFilterMode:false})
                  }
                }}>
              </input>
            </div>

            <div className='groupedFilterDates'>
              <div className='dateClassifier'>Desde</div>
              <div className='dateClassifier'>Hasta</div>
              <Suspense fallback={<input className='datePicker' disabled readOnly aria-label="loading-start-date" />}>
                <DatePicker {...DATE_PICKER_SAFE_PROPS} className='datePicker' selectsStart selected={this.state.startDateToDisplay} onChange={(date)=>this.setState({startDate:date.getTime(),startDateToDisplay:date,llmFilterMode:false})} />
              </Suspense>
              <Suspense fallback={<input className='datePicker' disabled readOnly aria-label="loading-end-date" />}>
                <DatePicker {...DATE_PICKER_SAFE_PROPS} className='datePicker' selectsEnd selected={this.state.endDateToDisplay} onChange={(date)=>this.setState({endDate:date.getTime(),endDateToDisplay:date,llmFilterMode:false})} />
              </Suspense>
            </div>
            <button onClick={()=>this.setState({sendBoxLimitsHide:true})} className='sendBoxMinimize groupedFilterClose'>Cerrar</button>
          </div>);}
          else if(this.state.dataRegister.length>0 && this.state.sendBoxLimitsHide===true && this.checkIfUserIsPremium()){
            return(
              <button onClick={()=>this.setState({sendBoxLimitsHide:false})} className='sendBox sendBoxLimits sendBoxMinimizeDims filterBox groupedFilterToggle' id="theSendBox">
                Filtrar
              </button>
            );
            }
          }

          renderNumericLimitsRepo(){
            if(this.state.repoData.length>0 && this.checkIfUserIsPremium() && this.state.toggleBg==='toggleActive'){
              return(
                <div 
                  onMouseDown={()=>this.holdTheMapStill()}
                  onTouchStart={()=>this.holdTheMapStill()}
                  onMouseUp={()=>this.releaseTheMap()}
                  onTouchEnd={()=>this.releaseTheMap()}
                className='sendBox sendBoxLimits sendBoxLimitsRepo' id="theSendBox">
                  {this.getMinMaxDateRangeRepo()}
                  
                  <div className='dateClassifier'>Desde Fecha</div>
                  <Suspense fallback={<input className='datePicker makeSmaller' disabled readOnly aria-label="loading-start-date-repo" />}>
                    <DatePicker
                      {...DATE_PICKER_SAFE_PROPS}
                      className='datePicker makeSmaller'
                      selectsStart
                      selected={this.state.startDateToDisplayRepo || null}
                      startDate={this.state.startDateToDisplayRepo || null}
                      endDate={this.state.endDateToDisplayRepo || null}
                      open={this.state.repoStartDatePickerOpen}
                      popperPlacement="bottom-start"
                      onFocus={()=>this.setState({repoStartDatePickerOpen:true, repoEndDatePickerOpen:false})}
                      onInputClick={()=>this.setState({repoStartDatePickerOpen:true, repoEndDatePickerOpen:false})}
                      onClickOutside={()=>this.setState({repoStartDatePickerOpen:false})}
                      onCalendarClose={()=>this.setState({repoStartDatePickerOpen:false})}
                      shouldCloseOnSelect
                      onChange={(date)=>{
                        if (!date) return;
                        this.setState({
                          startDateRepo:date.getTime(),
                          startDateToDisplayRepo:date,
                          repoStartDatePickerOpen:false
                        });
                      }}
                    />
                  </Suspense>
                  <div className='dateClassifier'>Hasta Fecha</div>
                  <Suspense fallback={<input className='datePicker makeSmaller' disabled readOnly aria-label="loading-end-date-repo" />}>
                    <DatePicker
                      {...DATE_PICKER_SAFE_PROPS}
                      className='datePicker makeSmaller'
                      selectsEnd
                      selected={this.state.endDateToDisplayRepo || null}
                      startDate={this.state.startDateToDisplayRepo || null}
                      endDate={this.state.endDateToDisplayRepo || null}
                      open={this.state.repoEndDatePickerOpen}
                      popperPlacement="bottom-start"
                      onFocus={()=>this.setState({repoStartDatePickerOpen:false, repoEndDatePickerOpen:true})}
                      onInputClick={()=>this.setState({repoStartDatePickerOpen:false, repoEndDatePickerOpen:true})}
                      onClickOutside={()=>this.setState({repoEndDatePickerOpen:false})}
                      onCalendarClose={()=>this.setState({repoEndDatePickerOpen:false})}
                      shouldCloseOnSelect
                      onChange={(date)=>{
                        if (!date) return;
                        this.setState({
                          endDateRepo:date.getTime(),
                          endDateToDisplayRepo:date,
                          repoEndDatePickerOpen:false
                        });
                      }}
                    />
                  </Suspense>
                </div>);}
                }

    handleDeleteShape=()=>{
    this.decodeToken()
    this.fetchTrials()
    this.removePulsePolygonLayer()
    const clearPointsState = {
      setCoords:'',
      dataRegister:[],
      filteredDataRegister:[],
      masterDataRegister:[],
      llmFilterMode:false,
      startDate:"",
      endDate:"",
      maxPrice:999999999,
      minPrice:0,
      minStruct:0,
      maxStruct:999999999,
      pointsPaneClass:'fade-in',
      lastSearchPolygon:null,
      saveAreaPromptOpen:false,
      saveAreaStatus:'',
    };

    if ((this.state.dataRegister || []).length > 0) {
      this.setState({ pointsPaneClass: 'fade-out' });
      setTimeout(() => {
        this.setState(clearPointsState);
      }, 260);
    } else {
      this.setState(clearPointsState);
    }
    
    document.querySelector("#root > div > div > div.leaflet-control-container > div.leaflet-top.leaflet-left > div.leaflet-draw.leaflet-control > div:nth-child(1)").style="";
    }   

    getGeoLocateErrorMessage(reasonCode = "unknown") {
      const isEs = this.state.selectedLanguage === "spanish";
      if (reasonCode === "unsupported") {
        return isEs ? "Tu navegador no soporta GPS." : "Your browser does not support geolocation.";
      }
      if (reasonCode === "insecure_context") {
        return isEs ? "GPS requiere HTTPS para funcionar." : "Geolocation requires HTTPS to work.";
      }
      if (reasonCode === "permission_denied") {
        return isEs ? "Permiso de ubicacion denegado." : "Location permission was denied.";
      }
      if (reasonCode === "position_unavailable") {
        return isEs ? "No se pudo obtener tu ubicacion." : "Your location could not be determined.";
      }
      if (reasonCode === "timeout") {
        return isEs ? "Tiempo de espera agotado al obtener GPS." : "Timed out while getting your location.";
      }
      if (reasonCode === "invalid_position") {
        return isEs ? "Ubicacion recibida no valida." : "Received an invalid location.";
      }
      return isEs ? "No se pudo obtener tu ubicacion GPS." : "Unable to get your GPS location.";
    }

    scheduleGeoLocateErrorClear() {
      try {
        if (this.gpsErrorTimer) clearTimeout(this.gpsErrorTimer);
      } catch {}
      this.gpsErrorTimer = setTimeout(() => {
        this.gpsErrorTimer = null;
        this.setState({ geoLocateError: "" });
      }, 5500);
    }

    scheduleGpsFocusClear() {
      try {
        if (this.gpsMarkerTimer) clearTimeout(this.gpsMarkerTimer);
      } catch {}
      this.gpsMarkerTimer = setTimeout(() => {
        this.gpsMarkerTimer = null;
        this.setState({ gpsFocusPoint: null });
      }, 8000);
    }

    handleGeoLocateFailure(reasonCode = "unknown", rawError = null) {
      const message = this.getGeoLocateErrorMessage(reasonCode);
      this.setState({ geoLocating: false, geoLocateError: message });
      this.scheduleGeoLocateErrorClear();
      trackEvent("gps_locate_failed", { reason: reasonCode });
      if (this.behaviorTracker) {
        this.behaviorTracker.trackCustomAction("db_gps_locate_failed", { reason: reasonCode });
      }
      if (rawError) {
        this.reportFrontendError("gps_locate_failed", rawError, {
          source: "gps_locate",
          extra: { reason: reasonCode }
        });
      }
    }

    locateUserPosition() {
      if (this.state.geoLocating === true) return;

      trackEvent("gps_locate_requested");
      if (this.behaviorTracker) {
        this.behaviorTracker.trackCustomAction("db_gps_locate_requested");
      }

      if (typeof window !== "undefined" && !window.isSecureContext && window.location?.hostname !== "localhost" && window.location?.hostname !== "127.0.0.1") {
        this.handleGeoLocateFailure("insecure_context");
        return;
      }

      if (!navigator?.geolocation) {
        this.handleGeoLocateFailure("unsupported");
        return;
      }

      this.setState({ geoLocating: true, geoLocateError: "" });
      try {
        if (this.gpsErrorTimer) {
          clearTimeout(this.gpsErrorTimer);
          this.gpsErrorTimer = null;
        }
      } catch {}

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = Number(position?.coords?.latitude);
          const lng = Number(position?.coords?.longitude);
          const accuracy = Number(position?.coords?.accuracy);

          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            this.handleGeoLocateFailure("invalid_position", position);
            return;
          }

          const map = this.mapRef.current;
          const baseZoom = Number(map?.getZoom?.() || 0);
          const targetZoom = Math.max(baseZoom, 17);
          try {
            if (map?.flyTo) {
              map.flyTo([lat, lng], targetZoom, { animate: true, duration: 1.1 });
            } else if (map?.setView) {
              map.setView([lat, lng], targetZoom, { animate: true });
            }
          } catch {}

          this.setState({
            geoLocating: false,
            geoLocateError: "",
            initialCoords: [lat, lng],
            gpsFocusPoint: {
              lat,
              lng,
              token: `gps_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
            }
          });
          this.scheduleGpsFocusClear();

          trackEvent("gps_locate_success", {
            accuracy_meters: Number.isFinite(accuracy) ? Math.round(accuracy) : undefined,
            target_zoom: targetZoom
          });
          if (this.behaviorTracker) {
            this.behaviorTracker.trackCustomAction("db_gps_locate_success", {
              accuracy_meters: Number.isFinite(accuracy) ? Math.round(accuracy) : undefined
            });
          }
        },
        (error) => {
          let reasonCode = "unknown";
          if (Number(error?.code) === 1) reasonCode = "permission_denied";
          if (Number(error?.code) === 2) reasonCode = "position_unavailable";
          if (Number(error?.code) === 3) reasonCode = "timeout";
          this.handleGeoLocateFailure(reasonCode, error);
        },
        {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 30000
        }
      );
    }

    renderGpsLocateControl() {
      if (this.state.loggedIn !== true) return null;
      const isEs = this.state.selectedLanguage === "spanish";
      const titleText = isEs ? "Centrar mapa en mi ubicacion" : "Center map on my location";
      const compactLabel = this.state.geoLocating ? "..." : "GPS";

      return (
        <MapEventGuardLegacy as="div" className="gpsLocateControl leaflet-control" id="gpsLocateControl">
          <button
            type="button"
            className={`gpsLocateBtn ${this.state.geoLocating ? "is-loading" : ""}`}
            onClick={() => this.locateUserPosition()}
            disabled={this.state.geoLocating}
            aria-label={titleText}
            title={titleText}
          >
            <span className="gpsLocateBtnText">{compactLabel}</span>
          </button>
          <span className="gpsLocateHint" aria-hidden="true">Mi Ubicacion</span>
          {this.state.geoLocateError ? (
            <div className="gpsLocateError">{this.state.geoLocateError}</div>
          ) : null}
        </MapEventGuardLegacy>
      );
    }

    flyToLocation(coords) {
      try{this.mapRef.current.flyTo([coords.y,coords.x],17.5)}catch{}
      const source = String(coords?.source || "searchbar");
      trackSearchInteraction({
        searchType: source,
        isTrialSearch: false
      });
      trackEvent("location_search_used", {
        source
      });
    } 

    checkIfUserIsPremium(){
      return isPremium(this.state.decodeToken);
    }

    isPass72User(){
      return isPass72(this.state.decodeToken);
    }

    isProUser(){
      return isPro(this.state.decodeToken);
    }

    hasFullStatsAccess(){
      return hasPass72Access(this.state.decodeToken);
    }

    /* ---------------- PlusMAX Pro: saved areas + Area Pulse ---------------- */

    getAuthHeaders(extra = {}){
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.state.token || localStorage.getItem('token') || sessionStorage.getItem('token') || ''}`,
        ...extra
      };
    }

    fetchSavedAreas(){
      if(!this.isProUser()){return Promise.resolve([]);}
      return fetch(`${this.state.serverIp}/api/savedAreas`, {
        headers: this.getAuthHeaders()
      })
        .then((res)=>res.json())
        .then((data)=>{
          const areas = Array.isArray(data?.areas) ? data.areas : [];
          this.setState({ savedAreas: areas });
          return areas;
        })
        .catch(()=>[]);
    }

    // Non-blocking login-time pulse prefetch for Pro users. The "Tus áreas"
    // panel remains closed until the user opens it from the map control.
    maybeFetchAreaPulse(){
      if(this.pulseFetchedForSession === true){return;}
      if(!this.isProUser()){return;}
      this.pulseFetchedForSession = true;

      this.fetchSavedAreas().then((areas)=>{
        if(!Array.isArray(areas) || areas.length === 0){
          return;
        }
        fetch(`${this.state.serverIp}/api/savedAreas/pulse`, {
          headers: this.getAuthHeaders()
        })
          .then((res)=>res.json())
          .then((pulse)=>{
            if(!pulse || !Array.isArray(pulse.areas)){return;}
            this.setState({ proPulse: pulse });
          })
          .catch((error)=>{
            this.reportFrontendError('area_pulse_fetch_failed', error, {
              source: 'area_pulse',
              endpoint: '/api/savedAreas/pulse'
            });
          });
      });
    }

    openSaveAreaPrompt(){
      if(!this.isProUser()){
        trackEvent('pro_upsell_seen', { surface: 'save_area' });
        trackBeginCheckout({ plan: 'PlusMAXPro', source: 'save_area_upsell' });
        this.setState({ pageIndex: 'account', subscriptionIndex: 'PlusMAXPro' });
        return;
      }
      this.setState({ saveAreaPromptOpen: true, saveAreaName: '', saveAreaStatus: '', showSaveAreaHint: false });
    }

    submitSaveArea(){
      const name = String(this.state.saveAreaName || '').trim().slice(0, 60);
      if(!name){
        trackEvent('saved_area_create_blocked', { reason: 'missing_name' });
        this.setState({ saveAreaStatus: this.languageIndexer('Escribe un nombre para el área.', 'Type a name for the area.') });
        return;
      }
      const rings = this.state.lastSearchPolygon;
      if(!rings){
        trackEvent('saved_area_create_blocked', { reason: 'missing_polygon' });
        this.setState({ saveAreaStatus: this.languageIndexer('No hay un polígono de búsqueda activo.', 'There is no active search polygon.') });
        return;
      }
      trackEvent('saved_area_create_started');
      fetch(`${this.state.serverIp}/api/savedAreas`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ name, rings })
      })
        .then((res)=>res.json().then((data)=>({ ok: res.ok, status: res.status, data })))
        .then(({ ok, data })=>{
          if(!ok || !data?.area){
            if(data?.error === 'saved_area_limit_reached'){
              throw new Error(this.languageIndexer(
                'Alcanzaste el máximo de 10 áreas guardadas. Borra una para guardar otra.',
                'You reached the maximum of 10 saved areas. Delete one to save another.'
              ));
            }
            throw new Error(this.languageIndexer('No se pudo guardar el área.', 'The area could not be saved.'));
          }
          trackEvent('saved_area_created', { area_id: data.area.id });
          this.setState((prev)=>({
            savedAreas: [...(prev.savedAreas || []), data.area],
            saveAreaPromptOpen: false,
            saveAreaName: '',
            saveAreaStatus: ''
          }));
        })
        .catch((error)=>{
          trackEvent('saved_area_create_failed');
          this.setState({ saveAreaStatus: String(error?.message || 'save_failed') });
        });
    }

    // "Ver en mapa": rehydrate the saved polygon into the existing search flow —
    // same polygon state as _onCreate, drawn on the Leaflet map, then the same
    // search path as the search button, then mark the area viewed.
    async openSavedAreaOnMap(area){
      const rings = Array.isArray(area?.rings) ? area.rings : null;
      if(!rings || !rings[0] || rings[0].length < 3){return;}

      this.setState({ showAreaPulse: false, pageIndex: '' });

      try{
        const map = this.mapRef.current;
        if(map){
          this.removePulsePolygonLayer();
          const leaflet = await import('leaflet');
          const L = leaflet.default || leaflet;
          const latlngs = rings[0].map((coord)=>[coord[1], coord[0]]);
          const polygon = L.polygon(latlngs, { color: '#3388ff', weight: 3 });
          // Join the draw FeatureGroup so the native "Borrar Selección"
          // toolbar button activates and deleting it flows through the same
          // onDeleted -> handleDeleteShape path as a hand-drawn polygon.
          const drawGroup = this.drawFeatureGroupRef.current;
          if (drawGroup && typeof drawGroup.addLayer === 'function') {
            drawGroup.addLayer(polygon);
          } else {
            polygon.addTo(map);
          }
          this.pulsePolygonLayer = polygon;
          map.fitBounds(polygon.getBounds(), { padding: [40, 40] });
        }
      }catch(error){
        this.reportFrontendError('saved_area_draw_failed', error, { source: 'area_pulse' });
      }

      // Same state shape _onCreate produces, then the same search entry point.
      this.setState({
        setCoords: JSON.stringify(rings),
        toggleBg: 'toggleInactive'
      }, ()=>{
        this.sendRequest();
      });

      fetch(`${this.state.serverIp}/api/savedAreas/${area.id}/view`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      }).catch(()=>{});
    }

    // "Guardar esta área" lives in the StatsArea header, right beside the
    // "Generar informe" button (moved out of the map overlay because it
    // conflicted with the map control buttons).
    renderSaveAreaControl(){
      if(this.state.loggedIn !== true){return null;}
      // Conversion surface: active for Pro, visible-but-locked for PlusMAX.
      const pro = this.isProUser();
      if(!pro && !hasPlusMaxAccess(this.state.decodeToken)){return null;}
      if(!this.state.lastSearchPolygon){return null;}

      return (
        <>
          <div
            onClick={(e)=>{ e.preventDefault(); this.openSaveAreaPrompt(); }}
            className={`statsHeaderBtn ${pro ? 'statsHeaderBtn--secondary' : 'statsHeaderBtn--locked'} saveAreaHeaderBtn`}
            role='button'
            tabIndex={0}
            onKeyDown={(e)=>(e.key === 'Enter' ? this.openSaveAreaPrompt() : null)}
            title={pro
              ? this.languageIndexer('Guardar esta área para tu resumen diario', 'Save this area for your daily summary')
              : this.languageIndexer('Disponible con PlusMAX Pro', 'Available with PlusMAX Pro')}
          >
            <span className='statsHeaderBtnIcon' aria-hidden='true'>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z' />
              </svg>
            </span>
            <span className='statsHeaderBtnLabel statsHeaderBtnLabel--full'>
              {pro
                ? this.languageIndexer('Guardar Área', 'Save Area')
                : this.languageIndexer('🔒 Guardar Área (Pro)', '🔒 Save Area (Pro)')}
            </span>
            <span className='statsHeaderBtnLabel statsHeaderBtnLabel--short'>
              {pro
                ? this.languageIndexer('Guardar', 'Save')
                : this.languageIndexer('🔒 Guardar (Pro)', '🔒 Save (Pro)')}
            </span>
          </div>

          {this.state.saveAreaPromptOpen && pro ? (
            <div
              style={{position:'fixed', inset:0, zIndex:6500, background:'rgba(7,12,22,.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:12}}
              onClick={()=>this.setState({ saveAreaPromptOpen:false, saveAreaStatus:'' })}
            >
              <div
                style={{background:'#0f151f', border:'1px solid #223148', borderRadius:12, padding:14, width:'min(300px, 100%)'}}
                onClick={(e)=>e.stopPropagation()}
              >
                <div style={{color:'#e6eefc', fontSize:13, fontWeight:700, marginBottom:8}}>
                  {this.languageIndexer('Nombre del área', 'Area name')}
                </div>
                <input
                  value={this.state.saveAreaName}
                  maxLength={60}
                  autoFocus
                  onChange={(e)=>this.setState({ saveAreaName: e.target.value })}
                  onKeyDown={(e)=>(e.key === 'Enter' ? this.submitSaveArea() : null)}
                  style={{width:'100%', boxSizing:'border-box', background:'#0c121a', border:'1px solid #223148', color:'#e6eefc', borderRadius:8, padding:'8px 10px', fontSize:13}}
                  placeholder={this.languageIndexer('p. ej., Condado', 'e.g., Condado')}
                />
                {this.state.saveAreaStatus ? (
                  <div style={{color:'#ff9d9d', fontSize:11, marginTop:6}}>{this.state.saveAreaStatus}</div>
                ) : null}
                <div style={{display:'flex', gap:8, marginTop:10}}>
                  <button type='button' className='saveAreaBtn' onClick={()=>this.submitSaveArea()}>
                    {this.languageIndexer('Guardar', 'Save')}
                  </button>
                  <button type='button' className='saveAreaBtn saveAreaBtnLocked' onClick={()=>this.setState({ saveAreaPromptOpen:false, saveAreaStatus:'' })}>
                    {this.languageIndexer('Cancelar', 'Cancel')}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {this.state.showSaveAreaHint && pro && !this.state.saveAreaPromptOpen ? (
            <div className='saveAreaHint saveAreaHintFixed'>
              {this.languageIndexer(
                'Guarda esta área y te contaremos cómo cambia cada vez que inicies sesión.',
                'Save this area and we will tell you how it changes each time you sign in.'
              )}
            </div>
          ) : null}
        </>
      );
    }

    // Remove the rehydrated saved-area polygon from wherever it lives
    // (draw FeatureGroup and/or map pane).
    removePulsePolygonLayer(){
      if(!this.pulsePolygonLayer){return;}
      try {
        const drawGroup = this.drawFeatureGroupRef.current;
        if (drawGroup && typeof drawGroup.removeLayer === 'function') {
          drawGroup.removeLayer(this.pulsePolygonLayer);
        }
      } catch {}
      try {
        if (this.mapRef.current) {
          this.mapRef.current.removeLayer(this.pulsePolygonLayer);
        }
      } catch {}
      this.pulsePolygonLayer = null;
    }

    renderAreaPulse(){
      if(this.state.showAreaPulse !== true || !this.isProUser()){return null;}
      return this.renderDeferred(
        <AreaPulse
          serverIp={this.state.serverIp}
          selectedLanguage={this.state.selectedLanguage}
          pulse={this.state.proPulse}
          areas={this.state.savedAreas}
          initialView={this.state.areaPulseView}
          onPulseUpdated={(pulse)=>this.setState({ proPulse: pulse })}
          onAreasChanged={()=>this.fetchSavedAreas()}
          onViewArea={(area)=>this.openSavedAreaOnMap(area)}
          onDismiss={()=>this.setState({ showAreaPulse:false })}
        />
      );
    }

    renderAreaPulseButton(){
      if(!this.isProUser()){return null;}
      return (
        <div style={{bottom:235,width:70}} onClick={()=>{
          sendAnalytics("MAP","OPEN AREA PULSE",String(new Date()))
          this.setState({ showAreaPulse:true, areaPulseView:'pulse' });
          if(!this.state.proPulse){
            this.pulseFetchedForSession = false;
            this.maybeFetchAreaPulse();
          }
        }} className='logOutBtn'>
          <strong>{this.languageIndexer('Tus áreas', 'Your areas')}</strong>
        </div>
      );
    }

    /* ------------------------------------------------------------------------ */

    toggleThumbRepo(){
      if(this.state.toggleBg==='toggleInactive'){
        this.setState({toggleBg:'toggleActive'})
        trackEvent("repo_layer_toggled", {
          enabled: true,
          plan_tier: this.getPlanTierFromDecoded(this.state.decodeToken)
        });
        if(this.state.repoData.length===0){this.getRepoData()}
        try{this.consumeTrials()}catch{}
        this.fetchTrials()
      }
      else{
        this.decodeToken()
        this.fetchTrials()
        this.setState({
          toggleBg:'toggleInactive',
          startDateRepo:'',
          endDateRepo: '',
          startDateToDisplayRepo:'',
          endDateToDisplayRepo:'',
        })
        trackEvent("repo_layer_toggled", {
          enabled: false,
          plan_tier: this.getPlanTierFromDecoded(this.state.decodeToken)
        });
      }
    }

    getRepoData(){
      let url = `${this.state.serverIp}/api/getRepoPoints`;
      const token = this.state.token;
      if (window.location.protocol !== "file:"){
        fetch(url,{
          method: "POST", // *GET, POST, PUT, DELETE, etc.
          mode: "cors", // no-cors, *cors, same-origin
          cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
          credentials: "same-origin", // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control':'no-cache',
            'Referrer':'https://casadata.io/',
            // Include the token in the Authorization header
            'Authorization': `Bearer ${token}`,
          },
          redirect: "follow", // manual, *follow, error
          // referrerPolicy:"no-referrer-when-downgrade",
        })
        .then(res=>res.json())
        .then(res=>{
          this.setState({repoData: Array.isArray(res?.response) ? res.response : []})
          trackEvent("repo_points_loaded", {
            result_count: Array.isArray(res?.response) ? res.response.length : 0
          });
        })
        .catch((err)=>{
          this.reportFrontendError("repo_points_failed", err, {
            source: "repo_layer",
            endpoint: "/api/getRepoPoints"
          });
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          window.location.reload();
        })  
    }}

    showRepoToggle(){
      if(hasPass72Access(this.state.decodeToken)){
        return(
          <div className='repoToggle'>
            <div>
              {this.languageIndexer("Ver Reposeidas",'See Repossessed')}
            </div>
            <div onClick={()=>{
              trackEvent("repo_toggle_clicked", {
                plan_tier: this.getPlanTierFromDecoded(this.state.decodeToken)
              });
              if(this.state.trials===0){this.decodeToken()}
              this.toggleThumbRepo()
              // this.fetchTrials()
              // this.consumeTrials()
              }} className={`toggleBtn ${this.state.toggleBg}`}>
              <div className='toggleThumb'></div>
            </div>
          </div>
        );
      }
      else{
        return(
          <div className='repoToggle'>
            <div>{this.languageIndexer("Ver Reposeidas",'See Repossessed')}</div>
            <div onClick={()=>{
              trackEvent("repo_toggle_clicked", {
                plan_tier: this.getPlanTierFromDecoded(this.state.decodeToken)
              });
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","PLUSMAX PROMPT BY FREE REPOTOGGLE",String(new Date()))
              // this.routeToPlusMax()
              const remainingTrials = Number(this.state.trials);
              if(!Number.isFinite(remainingTrials) || remainingTrials <= 0){
                trackEvent("plusmax_prompt_shown", { trigger: "repo_toggle" });
                this.setState({pageIndex:'plusMaxPrompt'});
              }
              else{
                this.turnOnPlusMaxMode()
                this.toggleThumbRepo()
                sendAnalytics("HOMEPAGE","PlusMAX Trial Repo Search",String(new Date()))
                trackEvent("trial_repo_search_started");
              }
              }} className={`toggleBtn ${this.state.toggleBg}`}>
              <div className='toggleThumb'></div>
            </div>
          </div>
        );
      }
    }

    languageSelector(){
      if(this.state.selectedLanguage==='spanish'){
        return(
          <div className="language-selector2">
                <button
                    className={this.state.selectedLanguage === 'english' ? 'active' : ''}
                    onClick={()=>{
                      this.setState({selectedLanguage:'english',searchPlaceholder:'Search for address here'});
                      trackEvent("language_changed", { language: "english", source: "map_ui" });
                    }}
                >
                <div className='languageFix2'>English</div><img src={usaflag} height='20px' width='20px' alt='' loading='lazy' decoding='async' />
                </button>
          </div>
        );
      }
      else{
        return(
          <div className="language-selector2">
                <button
                    className={this.state.selectedLanguage === 'spanish' ? 'active' : ''}
                    onClick={()=>{
                      this.setState({selectedLanguage:'spanish',searchPlaceholder:'Buscar Direccion Aquí'});
                      trackEvent("language_changed", { language: "spanish", source: "map_ui" });
                    }}
                >
                <div className='languageFix2'>Español</div><img src={prflag} height='20px' width='20px' alt='' loading='lazy' decoding='async' />
                </button>
          </div>
        );
      }
    }

    routeToPass72(source = 'upgrade_cta'){
      trackBeginCheckout({
        plan: "Pass72",
        billing: "one_time",
        source
      });
      this.setState({pageIndex:'account',subscriptionIndex:'Pass72'});
    }

    // ---- Auto explore mode (viewport points + one-click complex selection) ----

    setSelectionMode(mode){
      const nextMode = mode === 'auto' ? 'auto' : 'manual';
      if (nextMode === this.state.selectionMode) return;
      try { localStorage.setItem('cd_map_mode', nextMode); } catch {}
      trackEvent('map_mode_changed', { mode: nextMode });
      if (this.behaviorTracker) {
        this.behaviorTracker.trackCustomAction('map_mode_changed', { mode: nextMode });
      }
      this.clearSelectionForModeSwitch();
      this.autoSelectedCatastros = new Set();
      this.autoNoMatchCatastros = new Set();
      this.setState({ selectionMode: nextMode, autoPointsLoading: false });
    }

    // Leaving a mode discards its active selection so the destination mode
    // always starts from a blank map.
    clearSelectionForModeSwitch(){
      if (this.state.selectionMode === 'auto') {
        if (this.state.autoComplex || (this.state.dataRegister || []).length > 0) {
          this.clearAutoComplex();
        }
        return;
      }
      const drawGroup = this.drawFeatureGroupRef.current;
      const hasDrawnShapes = Boolean(
        drawGroup && typeof drawGroup.getLayers === 'function' && drawGroup.getLayers().length > 0
      );
      const hasManualSelection = hasDrawnShapes
        || (this.state.dataRegister || []).length > 0
        || Boolean(this.state.lastSearchPolygon);
      if (!hasManualSelection) return;
      if (drawGroup && typeof drawGroup.clearLayers === 'function') {
        try { drawGroup.clearLayers(); } catch {}
      }
      this.handleDeleteShape();
    }

    clearAutoComplex(){
      this.autoSelectedCatastros = new Set();
      this.setState({
        autoComplex: null,
        dataRegister: [],
        masterDataRegister: [],
        filteredDataRegister: [],
        llmFilterMode: false
      });
    }

    routeToAutoComplexUpsell(){
      trackEvent('complex_select_locked', { source: 'auto_mode' });
      trackBeginCheckout({ plan: 'PlusMAX', source: 'auto_complex_upsell' });
      this.setState({ pageIndex: 'account', subscriptionIndex: 'PlusMAX' });
    }

    handleAutoSelectComplex(feature){
      // Complex selection (and therefore any property data) is Pass72+;
      // explore points themselves are coords-only so nothing leaks before
      // this gate.
      if (!hasPass72Access(this.state.decodeToken)) {
        this.routeToAutoComplexUpsell();
        return;
      }
      const attrs = feature?.attributes || {};
      const catastro = String(attrs.CATASTRO || '').trim();
      if (!catastro || this.state.autoComplexLoading) return;
      if (this.autoSelectedCatastros?.has(catastro) || this.autoNoMatchCatastros?.has(catastro)) return;
      this.setState({ autoComplexLoading: true });
      fetch(`${this.state.serverIp}/api/getComplexProperties`, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.state.token}`
        },
        body: JSON.stringify({ catastro })
      })
        .then((res) => res.json())
        .then((res) => {
          if (this.state.selectionMode !== 'auto') {
            // The user left auto mode while this complex was loading; the
            // switch already cleared the selection, so drop the response.
            this.setState({ autoComplexLoading: false });
            return;
          }
          if (res?.upgradeRequired === true) {
            this.setState({ autoComplexLoading: false });
            this.routeToAutoComplexUpsell();
            return;
          }
          if (!res?.complex || !Array.isArray(res?.features) || res.features.length === 0) {
            trackEvent('select_complex_no_match', { source: 'auto_mode' });
            if (!this.autoNoMatchCatastros) this.autoNoMatchCatastros = new Set();
            this.autoNoMatchCatastros.add(catastro);
            this.setState({
              autoComplexLoading: false,
              autoComplex: { name: '', prefix: '', count: 0, noMatch: true }
            });
            window.setTimeout(() => {
              this.setState((prev) => (prev.autoComplex?.noMatch ? { autoComplex: null } : null));
            }, 4000);
            return;
          }
          const rows = this.buildComparableRows(res.features);
          trackEvent('select_complex', {
            members: rows.length,
            parcel_count: Number(res.complex.parcelCount || 0),
            prefix: String(res.complex.prefix || '')
          });
          trackEvent('view_search_results', {
            search_mode: 'auto_complex',
            result_count: rows.length
          });
          if (this.behaviorTracker) {
            this.behaviorTracker.trackFunnelStage('complex_selected');
          }
          this.autoSelectedCatastros = new Set(
            rows.map((row) => String(row?.attributes?.CATASTRO || '').trim()).filter(Boolean)
          );
          this.setState({
            autoComplexLoading: false,
            autoComplex: {
              name: String(res.complex.name || '').toUpperCase(),
              prefix: String(res.complex.prefix || '').toUpperCase(),
              municipio: String(res.complex.municipio || ''),
              count: rows.length,
              parcelCount: Number(res.complex.parcelCount || 0)
            },
            masterDataRegister: rows,
            dataRegister: rows,
            filteredDataRegister: [],
            llmFilterMode: false
          });
        })
        .catch((err) => {
          this.setState({ autoComplexLoading: false });
          this.reportFrontendError('complex_select_failed', err, {
            source: 'auto_mode',
            endpoint: '/api/getComplexProperties'
          });
        });
    }

    renderMapModeToggle(){
      if (!this.state.loggedIn) return null;
      const isAuto = this.state.selectionMode === 'auto';
      return (
        <div className='mapModeToggleBlock'>
          <div className='mapModeLabel'>
            {this.languageIndexer('Modo Selección', 'Selection Mode')}
          </div>
          <div className='mapModeToggle mapModeToggleDock' role='tablist' aria-label={this.languageIndexer('Modo de selección', 'Selection mode')}>
            <button
              type='button'
              role='tab'
              aria-selected={!isAuto}
              className={`mapModeBtn ${!isAuto ? 'isActive' : ''}`}
              onClick={() => this.setSelectionMode('manual')}
            >
              {this.languageIndexer('Manual', 'Manual')}
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={isAuto}
              className={`mapModeBtn ${isAuto ? 'isActive' : ''}`}
              onClick={() => this.setSelectionMode('auto')}
            >
              {this.languageIndexer('Auto', 'Auto')}
            </button>
          </div>
        </div>
      );
    }

    renderAutoModeHud(){
      if (!this.state.loggedIn || this.state.selectionMode !== 'auto') return null;
      const autoComplex = this.state.autoComplex;
      return (
        <div className='mapModeHudWrap'>
          {!autoComplex ? (
            this.state.autoPointsLoading ? (
              <div className='autoPointsLoader' role='status' aria-live='polite'>
                <span className='autoPointsLoaderSpinner' aria-hidden='true' />
                <span className='autoPointsLoaderText'>
                  {this.languageIndexer('Buscando datos…', 'Retrieving data…')}
                </span>
              </div>
            ) : (
              <div className='mapModeHint'>
                {this.languageIndexer('Modo Auto: toca un punto para analizar todo su complejo', 'Auto mode: tap a point to analyze its whole complex')}
              </div>
            )
          ) : (
            <div className={`autoComplexBanner ${autoComplex.noMatch ? 'isNoMatch' : ''}`}>
              <span>
                {autoComplex.noMatch
                  ? this.languageIndexer('Sin complejo identificado para esta propiedad', 'No complex identified for this property')
                  : `${autoComplex.prefix} ${autoComplex.name} · ${autoComplex.count} ${this.languageIndexer('ventas', 'sales')}`}
              </span>
              {!autoComplex.noMatch ? (
                <button
                  type='button'
                  className='autoComplexClear'
                  aria-label={this.languageIndexer('Limpiar selección', 'Clear selection')}
                  onClick={() => this.clearAutoComplex()}
                >
                  ✕
                </button>
              ) : null}
            </div>
          )}
        </div>
      );
    }

    dismissMapModeIntro(){
      try { localStorage.setItem('cd_map_mode_intro_seen', '1'); } catch {}
      this.setState({ mapModeIntroSeen: true });
    }

    // One-time, dismissable popup so users landing in the new Auto default
    // understand the point-tap flow and know they can switch modes with the tab.
    renderMapModeIntro(){
      if (!this.state.loggedIn || this.state.mapModeIntroSeen) return null;
      if (typeof document === 'undefined' || !document.body) return null;
      // Portal to body: the Leaflet map container carries a filter/blur which
      // creates a containing block that would trap this fixed overlay inside
      // the narrow account-control column. Rendering to body escapes it so the
      // popup truly centers over the whole viewport.
      return createPortal((
        <div className='mapModeIntroOverlay' role='dialog' aria-modal='true' aria-label='Selection mode'>
          <div className='mapModeIntroCard'>
            <div className='mapModeIntroBadge'>{this.languageIndexer('Nuevo', 'New')}</div>
            <div className='mapModeIntroTitle'>
              {this.languageIndexer('CASADATA inicia en Modo Auto', 'CASADATA starts in Auto mode')}
            </div>
            <div className='mapModeIntroBody'>
              {this.languageIndexer(
                'Explora el mapa y toca cualquier punto para analizar todo su complejo al instante. ¿Prefieres dibujar tu propia área? Cambia entre Auto y Manual cuando quieras con la pestaña de Modo Selección.',
                'Explore the map and tap any point to instantly analyze its whole complex. Prefer to draw your own area? Switch between Auto and Manual anytime with the Selection Mode tab.'
              )}
            </div>
            <button
              type='button'
              className='mapModeIntroBtn'
              onClick={() => this.dismissMapModeIntro()}
            >
              {this.languageIndexer('Entendido', 'Got it')}
            </button>
          </div>
        </div>
      ), document.body);
    }

    routeToPlusMax(){
      if(this.state.toggleBg==='toggleInactive'){
        trackBeginCheckout({
          plan: "PlusMAX",
          source: "repo_toggle_prompt"
        });
        this.setState({toggleBg:'toggleActive'})
        setTimeout(()=>this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'}),1000)
      }
      else{
        this.setState({
          toggleBg:'toggleInactive',
          startDateRepo:'',
          endDateRepo: '',
          startDateToDisplayRepo:'',
          endDateToDisplayRepo:'',
        })
      }
    }

    renderPass72MapUpgradeButton(source = 'membership_box'){
      return(
        <div className='levelShow3 pass72MapUpgradeBtn' onClick={()=>{
          sendAnalytics("MAP","ROUTE TO PASS72 BY TAB UNDER TOGGLE",String(new Date()))
          this.routeToPass72(source);
          }} style={{background:'#12774e',padding:3,color:'white',fontWeight:700,borderRadius:'5px'}}>Pase 72h</div>
      );
    }

    decideIfRenderTrials(){
      if(this.state.trials>0){
        return(
          <div className='membershipBoxLightExtended'>
            <div style={{display:'flex'}}>
              <strong>
              {this.languageIndexer("Membresia:",'Membership:')}&nbsp;
              </strong>
                <div style={{color:'white'}} className='baselogo'>BASE</div>
            </div>
            <div className='plusMaxTemp'>
            {this.languageIndexer("Quedan",'')} <span style={{fontWeight:700}}>{this.state.trials}</span> {this.languageIndexer("busquedas PlusMAX ",'PlusMAX Searches Remaining')}
            </div>
            {this.showRepoToggle()}
            <div style={{fontWeight:700}}>
              {this.languageIndexer("Upgrade A",'Upgrade To')}
            </div>
            <div className='levelShow3' onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO PLUS MAX BY TAB UNDER TOGGLE",String(new Date()))
              trackBeginCheckout({
                plan: "PlusMAX",
                source: "membership_box"
              });
              this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'})
              }} style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            {this.renderPass72MapUpgradeButton('membership_box_trial')}
            {/* <div className='levelShow3' onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO PLUS BY TAB UNDER TOGGLE",String(new Date()))
              this.setState({pageIndex:'account',subscriptionIndex:'Plus'})
              }} style={{background:'#273c75',padding:3,color:'white',fontWeight:700,borderRadius:'5px'}}>Plus</div> */}
              {this.languageSelector()}
          </div>
        );
      }
      else{
        return(
          <div className='membershipBoxLight'>
            <div style={{display:'flex'}}>
              <strong>
              {this.languageIndexer("Membresia:",'Membership:')}&nbsp;
              </strong>
                <div style={{color:'white'}} className='baselogo'>BASE</div>
            </div>
            {this.showRepoToggle()}
            <div style={{fontWeight:700}}>
              {this.languageIndexer("Upgrade A",'Upgrade To')}
            </div>
            <div className='levelShow3' onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO PLUS MAX BY TAB UNDER TOGGLE",String(new Date()))
              trackBeginCheckout({
                plan: "PlusMAX",
                source: "membership_box"
              });
              this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'})
              }} style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            {this.renderPass72MapUpgradeButton('membership_box_base')}
            {/* <div className='levelShow3' onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO PLUS BY TAB UNDER TOGGLE",String(new Date()))
              this.setState({pageIndex:'account',subscriptionIndex:'Plus'})
              }} style={{background:'#273c75',padding:3,color:'white',fontWeight:700,borderRadius:'5px'}}>Plus</div> */}
              {this.languageSelector()}
          </div>
        );
      }
    }

    formatPassRemaining(){
      const raw = Number(this.state.decodeToken?.passExpiresAt || 0);
      if (!Number.isFinite(raw) || raw <= 0) return '';
      const minutes = Math.max(0, Math.ceil(((raw * 1000) - Date.now()) / 60000));
      if (minutes <= 0) return this.languageIndexer('expirado', 'expired');
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }

    isTrialSession(decoded = this.state.decodeToken){
      try {
        const d = decoded || {};
        return String(d.username || '').startsWith('trial-') || String(d.initial || '').toUpperCase() === 'TEMPORARY';
      } catch { return false; }
    }

    formatTrialRemaining(){
      const raw = Number(this.state.decodeToken?.exp || 0);
      if (!Number.isFinite(raw) || raw <= 0) return '';
      const minutes = Math.max(0, Math.ceil(((raw * 1000) - Date.now()) / 60000));
      if (minutes <= 0) return this.languageIndexer('expirado', 'expired');
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }

    // Trial sessions cannot check out in-app (the synthetic trial account is
    // not a real user), so upgrades go through the public prices flow. The
    // trial token stays in storage: the resume link or a refresh restores the
    // still-valid session if checkout is abandoned.
    routeTrialToPrices = (source = 'trial_countdown') => {
      this.setState({
        loggedIn: false,
        pageIndex: "",
        loginBtnContainerShow: "loginBtnContainer",
        frontpageTarget: "prices",
        frontpageReason: "trial_upgrade",
        frontpageSrc: source,
      });
      try { trackCustom("ViewPricing", { reason: "trial_upgrade", src: source }); } catch {}
      trackEvent("view_pricing", { reason: "trial_upgrade", source });
      try { sendAnalytics("PRICES", "TRIAL_UPGRADE_ROUTE", String(new Date())); } catch {}
    };

    resumeTrialFromPrices = () => {
      if (!this.isTrialSession()) return;
      trackEvent("trial_resume_from_prices", { source: "frontpage_notice" });
      this.setState({
        loggedIn: true,
        frontpageTarget: "",
        frontpageReason: "",
        frontpageSrc: "",
      });
    };

    renderMemberBox(){
      if(this.isTrialSession()){
        const remaining = this.formatTrialRemaining();
        return(
        <div className='membershipBox membershipBoxPass72 membershipBoxTrial'>
          <div className='membershipBoxPass72Header' style={{display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center'}}>
            <div><strong>{this.languageIndexer("Membresia",'Membership')}</strong></div>
            <div className='levelShow' style={{background:'#138a5a',padding:3,color:'white',fontWeight:700,borderRadius:'5px'}}>
              {this.languageIndexer('Prueba PlusMAX','PlusMAX Trial')}
            </div>
            {remaining ? <div className='membershipBoxPass72Expiry' style={{fontSize:11,fontWeight:700,color:'black'}}>{this.languageIndexer('Expira en','Expires in')} {remaining}</div> : null}
          </div>
          {this.showRepoToggle()}
          <div className='membershipBoxPass72UpgradeLabel' style={{fontWeight:700}}>
            {this.languageIndexer('Mantén tu acceso','Keep your access')}
          </div>
          <div className='levelShow3' onClick={()=>{
            sendAnalytics("MAP","TRIAL COUNTDOWN ROUTED TO PRICES",String(new Date()))
            this.routeTrialToPrices('membership_box_trial_countdown');
            }} style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>
            {this.languageIndexer('Ver planes','See plans')}
          </div>
          {this.languageSelector()}
        </div>
        );
      }
      if(isPro(this.state.decodeToken)){
        return(
        <div className='membershipBox'>
          <div style={{display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center'}}>
            <div><strong>
              {this.languageIndexer("Membresia",'Membership')}
            </strong></div>
            <div className='levelShow levelShowProBadge' style={{padding:3}}>PlusMAX Pro</div>
          </div>
          {this.showRepoToggle()}
          {this.languageSelector()}
        </div>
        );
      }
      else if(hasPlusMaxAccess(this.state.decodeToken)){
        return(
        <div className='membershipBox'>
          <div style={{display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center'}}>
            <div><strong>
              {this.languageIndexer("Membresia",'Membership')}
            </strong></div>
            <div className='levelShow' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
          </div>
          {this.showRepoToggle()}
          {this.languageSelector()}
        </div>
        );
      }
      else if(isPass72(this.state.decodeToken)){
        const remaining = this.formatPassRemaining();
        return(
        <div className='membershipBox membershipBoxPass72'>
          <div className='membershipBoxPass72Header' style={{display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center'}}>
            <div><strong>{this.languageIndexer("Membresia",'Membership')}</strong></div>
            <div className='levelShow' style={{background:'#12774e',padding:3,color:'white',fontWeight:700,borderRadius:'5px'}}>Pase 72h</div>
            {remaining ? <div className='membershipBoxPass72Expiry' style={{fontSize:11,fontWeight:700,color:'black'}}>{this.languageIndexer('Expira en','Expires in')} {remaining}</div> : null}
          </div>
          {this.showRepoToggle()}
          <div className='membershipBoxPass72UpgradeLabel' style={{fontWeight:700}}>Upgrade a</div>
          <div className='levelShow3' onClick={()=>{
            trackBeginCheckout({
              plan: "PlusMAX",
              source: "membership_box_pass_upgrade"
            });
            this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'});
          }} style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
          {this.languageSelector()}
        </div>
        );
      }
      else if(this.state.decodeToken.delta==='497CC0000'){
        return(
        <div className='membershipBox'>
          <div style={{
            display:'flex',
            justifyContent:'center',
            alignItems:'center'
          }}><strong>Membresia:&nbsp;</strong><div className='levelShow' style={{
            background:'#273c75',
            padding:3,
            color:'white',
            fontWeight:700,
            borderRadius:'5px'
            }}>Plus</div>
            </div>
            {this.showRepoToggle()}
            <div style={{fontWeight:700}}>Hacer Upgrade a</div>
            <div className='levelShow3' onClick={()=>{
              trackBeginCheckout({
                plan: "PlusMAX",
                source: "membership_box_plus_upgrade"
              });
              this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'})
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","PLUS MAX ROUTED BY TAB UNDER TOGGLE",String(new Date()))
              }} style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
        </div>
        );
      }
      else if(this.state.decodeToken.delta==='37485923'){
        return(
          this.decideIfRenderTrials()
        );
      }
    }

    displayOrHideStats(filteredData = this.getFilteredData()){
      if(filteredData.length>0&&this.hasFullStatsAccess()){
        return this.renderDeferred(
          <MapEventGuardLegacy as="div" 
            onMouseDown={()=>this.holdTheMapStill()}
            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          >
            <StatsArea
              onRequestClick={this.handleProgrammaticClick}
              onDeleteDataPoint={this.handleDeleteComparablePoint}
              selectedLanguage={this.state.selectedLanguage}
              dataRegister={filteredData}
              rawDataRegister={this.state.masterDataRegister}
              decodeToken={this.state.decodeToken}
              onDataRegisterChange={(newData, meta = {}) => {
                const nextRows = Array.isArray(newData) ? newData : [];
                this.setState((prev) => {
                  const patch = {
                    dataRegister: nextRows
                  };
                  if (meta && meta.source === "llm_filter") {
                    // Keep a temporary backup of the pre-filter register for quick restore/undo flows.
                    patch.filteredDataRegister = Array.isArray(prev.dataRegister) ? prev.dataRegister : [];
                    patch.llmFilterMode = true;
                    patch.llmRenderVersion = Number(prev.llmRenderVersion || 0) + 1;

                    let minRawDate = null;
                    let maxRawDate = null;
                    for (const row of nextRows) {
                      const raw = Number(row?.attributes?.SALESDTTM);
                      if (!Number.isFinite(raw)) continue;
                      const abs = Math.abs(raw);
                      if (!Number.isFinite(abs)) continue;
                      if (minRawDate == null || abs < minRawDate) minRawDate = abs;
                      if (maxRawDate == null || abs > maxRawDate) maxRawDate = abs;
                    }

                    patch.minPrice = 0;
                    patch.maxPrice = 999999999;
                    patch.minStruct = 0;
                    patch.maxStruct = 999999999;
                    if (minRawDate != null && maxRawDate != null) {
                      patch.startDate = toDateMs(minRawDate);
                      patch.endDate = toDateMs(maxRawDate);
                      patch.startDateToDisplay = toDateObject(minRawDate);
                      patch.endDateToDisplay = toDateObject(maxRawDate);
                    } else {
                      patch.startDate = "";
                      patch.endDate = "";
                      patch.startDateToDisplay = "";
                      patch.endDateToDisplay = "";
                    }
                  }
                  return patch;
                });
              }}
              serverIp={this.state.serverIp}
              searchPolygonRings={this.state.lastSearchPolygon}
              saveAreaNode={this.renderSaveAreaControl()}
              onRequestProUpgrade={(surface)=>{
                trackBeginCheckout({ plan: 'PlusMAXPro', source: `${surface || 'stats'}_upsell` });
                this.setState({ pageIndex:'account', subscriptionIndex:'PlusMAXPro' });
              }}
            />
          </MapEventGuardLegacy>
        );
      }
      else if(filteredData.length>0&&this.checkIfUserIsPremium()===false){
        return this.renderDeferred(
          <div 
            onMouseDown={()=>this.holdTheMapStill()}
            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          >
            <StatsAreaLight
              selectedLanguage={this.state.selectedLanguage}
              routeToPlus={()=>{
                trackBeginCheckout({ plan: "Plus", source: "stats_light_cta" });
                this.setState({pageIndex:'account',subscriptionIndex:'Plus'});
              }} 
              routeToPlusMax={()=>{
                trackBeginCheckout({ plan: "PlusMAX", source: "stats_light_cta" });
                this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'});
              }}
              routeToPass72={()=>this.routeToPass72('stats_light_cta')}
              dataRegister={filteredData}
            />
          </div>
        );
      }
      else if(filteredData.length>0&&this.state.decodeToken.delta==='497CC0000'){
        return this.renderDeferred(
          <div 
            onMouseDown={()=>this.holdTheMapStill()}
            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          >
            <StatsAreaMid dataRegister={filteredData}/>
          </div>
        );
      }
    }

    holdTheMapStill(){
      const map = this.mapRef.current;
      if (map && map.dragging) {
        map.dragging.disable();
      }
    }

    releaseTheMap(){
      const map = this.mapRef.current;
      if (map && map.dragging) {
        map.dragging.enable();
      }
    }

    getFilteredData() {
      // Hard guarantee for LLM transforms:
      // when an LLM filter is applied, render the transformed register directly.
      if (this.state.llmFilterMode === true) {
        return Array.isArray(this.state.dataRegister) ? this.state.dataRegister : [];
      }

      const normalizedRows = this.applyOutlierFilter(
        Array.isArray(this.state.dataRegister) ? this.state.dataRegister : []
      );

      const minPrice = this.state.minPrice === "" ? Number.NEGATIVE_INFINITY : Number(this.state.minPrice);
      const maxPrice = this.state.maxPrice === "" ? Number.POSITIVE_INFINITY : Number(this.state.maxPrice);
      const minStruct = this.state.minStruct === "" ? Number.NEGATIVE_INFINITY : Number(this.state.minStruct);
      const maxStruct = this.state.maxStruct === "" ? Number.POSITIVE_INFINITY : Number(this.state.maxStruct);
      const startDate = this.state.startDate === "" ? Number.NEGATIVE_INFINITY : Number(this.state.startDate);
      const endDate = this.state.endDate === "" ? Number.POSITIVE_INFINITY : Number(this.state.endDate);

      return normalizedRows.filter((data) => {
        let attributes = data.attributes || {};
        let indexedDate = formatDateYYYYMMDD(attributes.SALESDTTM);
        let indexedSalePrice = attributes.SALESAMT;
        let formatedDate = indexedDate ? new Date(indexedDate) : new Date(NaN);
        let indexedStruct = attributes.STRUCTURE;
        // Ensure none of the values are NaN or undefined
        if (typeof indexedSalePrice === 'undefined' || isNaN(indexedSalePrice)) return false;
        if (isNaN(formatedDate.getTime())) return false;
        if (typeof indexedStruct === 'undefined' || isNaN(indexedStruct)) return false;
    
        return indexedSalePrice >= minPrice 
            && indexedSalePrice <= maxPrice 
            && formatedDate.getTime() >= startDate 
            && formatedDate.getTime() <= endDate
            && indexedStruct >= minStruct
            && indexedStruct <= maxStruct;
      });
    }

    getLatestComparableSale(dataRegister = this.state.dataRegister) {
      if (!Array.isArray(dataRegister) || dataRegister.length === 0) return null;

      let maxSalesDTTMIndex = -1;
      let secondMaxSalesDTTMIndex = -1;
      let maxSalesDTTM = Number.NEGATIVE_INFINITY;
      let secondMaxSalesDTTM = Number.NEGATIVE_INFINITY;

      for (let i = 0; i < dataRegister.length; i += 1) {
        const currentSalesDTTM = Number(dataRegister[i]?.attributes?.SALESDTTM);
        if (!Number.isFinite(currentSalesDTTM)) continue;

        if (currentSalesDTTM > maxSalesDTTM) {
          secondMaxSalesDTTM = maxSalesDTTM;
          secondMaxSalesDTTMIndex = maxSalesDTTMIndex;
          maxSalesDTTM = currentSalesDTTM;
          maxSalesDTTMIndex = i;
        } else if (currentSalesDTTM > secondMaxSalesDTTM && currentSalesDTTM !== maxSalesDTTM) {
          secondMaxSalesDTTM = currentSalesDTTM;
          secondMaxSalesDTTMIndex = i;
        }
      }

      let lastElement = maxSalesDTTMIndex >= 0 ? dataRegister[maxSalesDTTMIndex] : null;
      if (lastElement && transactionType(lastElement.attributes) !== 'VENTA' && secondMaxSalesDTTMIndex >= 0) {
        lastElement = dataRegister[secondMaxSalesDTTMIndex];
      }

      return lastElement;
    }

    
    renderLoginBtns(){
      if(this.state.loggedIn===false){
        return(
          <MapEventGuardLegacy as='div'>
            <Frontpage
            landingTarget={this.state.frontpageTarget}
            landingReason={this.state.frontpageReason}
            landingSrc={this.state.frontpageSrc}
            onLandingHandled={() => this.setState({ frontpageTarget: "", frontpageReason: "", frontpageSrc: "" })}
            onResumeTrial={()=>this.resumeTrialFromPrices()}
            selectedLanguage={this.state.selectedLanguage}
            spanishSelected={()=>{
              this.setState({selectedLanguage:'spanish',searchPlaceholder:'Buscar Direccion Aquí'});
              trackEvent("language_changed", { language: "spanish", source: "frontpage" });
            }}
            englishSelected={()=>{
              this.setState({selectedLanguage:'english',searchPlaceholder:'Search for address here'});
              trackEvent("language_changed", { language: "english", source: "frontpage" });
            }}
            holdTheMapStill={()=>this.holdTheMapStill()}
            loginWindow={()=>this.setState({pageIndex:'login'})}
            signupWindow={()=>this.setState({pageIndex:'signup'})}
            termsWindowShow={()=>this.setState({pageIndex:'terms'})}
            contactWindowShow={()=>this.setState({pageIndex:'contact'})}
            policyWindowShow={()=>this.setState({pageIndex:'privacy'})}
            releaseTheMap={()=>this.releaseTheMap()}
            paidTierSelected={()=>this.setState({paidTierAtInit:true})}
            openDirectCheckout={(options = {}) => this.setState({
              pageIndex: 'directCheckout',
              directCheckoutBilling: (() => { const billing = String(options.billingCycle || '').toLowerCase(); return ['annual', 'pass72', 'pro_monthly', 'pro_annual'].includes(billing) ? billing : 'monthly'; })(),
              directCheckoutMagicToken: ''
            })}
            userSelectedDaily={()=>this.setState({selectedPayment:'daily'})}
            userSelectedMonthly={()=>this.setState({selectedPayment:'monthly'})}
            userSelectedWeekly={()=>this.setState({selectedPayment:'weekly'})}
            userSelectedAnnual={()=>this.setState({selectedPayment:'annual'})}
            serverIp={this.state.serverIp}
            fingerprint={this.state.fingerprint}
            />
          </MapEventGuardLegacy>
        );
      }
    }

	    renderLoginPages(){
	      if(this.state.pageIndex==='login'){
	        return this.renderPageTransition(
	          <div
	            onMouseDown={()=>this.holdTheMapStill()}
            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          >
            <GoogleOAuthProvider clientId='56161815321-ilaoghg7o0kqcbdmvdnbb94sj39bkf30.apps.googleusercontent.com'>
              <LoginPage
                fingerprint={this.state.fingerprint}
                selectedLanguage={this.state.selectedLanguage}
                serverIp={this.state.serverIp}
                userIsLoggedIn={(authToken)=>{
                  this.userIsLogedIn(authToken)
                  this.fetchTutorialPrompt()
                  this.fetchTrials()
                }}
                closeLoginWindow={()=>this.setState({pageIndex:''})}
                switchToForgotPw={()=>this.setState({pageIndex:'forgot'})}
	                userFirstLogin={()=>this.fetchTutorialPrompt()}
	              />
            </GoogleOAuthProvider>
	          </div>,
	          'login'
	        );
	      }
	      else if(this.state.pageIndex==='signup'){
	        return this.renderPageTransition(
	          <div
	            onMouseDown={()=>this.holdTheMapStill()}
            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          >
            <GoogleOAuthProvider clientId='56161815321-ilaoghg7o0kqcbdmvdnbb94sj39bkf30.apps.googleusercontent.com'>
              <SignUp
                userIsLoggedIn={()=>{
                  this.userIsLogedIn()
                  this.fetchTrials()
                }}
                selectedLanguage={this.state.selectedLanguage}
                serverIp={this.state.serverIp}
                switchToLoginWindow={()=>this.switchToLoginWindow()}
                routeToConditions={()=>this.setState({pageIndex:'terms'})}
                userFirstLogin={()=>{
                  if(!this.state.paidTierAtInit)
                    {this.fetchTutorialPrompt()}
                }}
                closeLoginWindow={()=>this.setState({
                  pageIndex:'',
	                  paidTierAtInit:false
	                })}
	              />
            </GoogleOAuthProvider>
	          </div>,
	          'signup'
	        );
	      }
	      else if(this.state.pageIndex==='forgot'){
	        return this.renderPageTransition(
	          <div
	            onMouseDown={()=>this.holdTheMapStill()}
	            onTouchStart={()=>this.holdTheMapStill()}
            onMouseUp={()=>this.releaseTheMap()}
            onTouchEnd={()=>this.releaseTheMap()}
          >
            <ForgotPassword
	              selectedLanguage={this.state.selectedLanguage}
	              closeLoginWindow={()=>this.setState({pageIndex:''})}
	            />
	          </div>,
	          'forgot'
	        );
	      }
	      else if(this.state.pageIndex==='directCheckout'){
	        return this.renderPageTransition(
	          <div
	            onMouseDown={()=>this.holdTheMapStill()}
	            onTouchStart={()=>this.holdTheMapStill()}
	            onMouseUp={()=>this.releaseTheMap()}
	            onTouchEnd={()=>this.releaseTheMap()}
	          >
	            <GoogleOAuthProvider clientId='56161815321-ilaoghg7o0kqcbdmvdnbb94sj39bkf30.apps.googleusercontent.com'>
	              <DirectCheckout
	                selectedLanguage={this.state.selectedLanguage}
	                serverIp={this.state.serverIp}
	                initialBillingCycle={this.state.directCheckoutBilling}
	                magicToken={this.state.directCheckoutMagicToken}
	                onMagicTokenConsumed={() => this.setState({ directCheckoutMagicToken: '' })}
	                closeCheckoutWindow={() => this.setState({
	                  pageIndex:'',
	                  directCheckoutMagicToken:'',
		                  directCheckoutBilling:'monthly'
		                })}
		              />
	            </GoogleOAuthProvider>
	          </div>,
	          'direct-checkout'
	        );
	      }
	    }
    
    switchToLoginWindow(){
      this.setState({pageIndex:'login'})
    }
    

    logOutUser(){
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      trackEvent("logout");
      clearAnalyticsUser();
      window.location.reload();
    }
    
    logOutBtn(){
      if(this.state.loggedIn===true){
        return(
          <section className='accountControl'>
            {this.renderMapModeIntro()}
            {this.renderMapModeToggle()}
            {this.renderAreaPulseButton()}
            <div style={{bottom:190,width:70}} onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO PR STATS",String(new Date()))
              this.setState({pageIndex:"stats"})
              }} className='logOutBtn'>
            <strong>
              {this.languageIndexer("Estadísticas Puerto Rico",'Puerto Rico Statistics')}
            </strong>
            </div>
            {this.decideIfShowLogOut()}
          </section>
        );
      }
    }

    decideIfShowLogOut(){
      if(this.state.decodeToken.initial!=='TEMPORARY'){
        return(<>
          <div style={{bottom:145,width:70}} onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO ACCOUNT",String(new Date()))
              this.setState({pageIndex:"account"})
              }} className='logOutBtn'>
            <strong>
              {this.languageIndexer("Mi Cuenta",'My Account')}
            </strong>
            </div>
            <div style={{bottom:100,width:70}} onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO TUTORIAL",String(new Date()))
              this.setState({pageIndex:"tutorial"})
              }} className='logOutBtn'>
            <strong>Tutorial</strong>
            </div>
            <div style={{bottom:55,width:70}} onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","LOG OUT",String(new Date()))
              this.logOutUser()
              }} className='logOutBtn'>
            <strong>Log Out</strong>
            </div></>
        );
      }
      else{
        return(<>
            <div style={{bottom:100,width:70}} onClick={()=>{
              //PAGE,ACTION,DATE
              sendAnalytics("MAP","ROUTE TO TUTORIAL",String(new Date()))
              this.setState({pageIndex:"tutorial"})
              }} className='logOutBtn'>
            <strong>Tutorial</strong>
            </div>
            </>);
      }
    }

	    accountPage(){
	      if(this.state.pageIndex==="account"){
	        return this.renderPageTransition(
	          <AccountPage
	            decodeToken={this.state.decodeToken}
	            selectedLanguage={this.state.selectedLanguage}
	            closeAccountWindow={()=>this.setState({pageIndex:'',toggleBg:'toggleInactive',subscriptionIndex:''})}
            holdTheMapStill={()=>this.holdTheMapStill()}
            releaseTheMap={()=>this.releaseTheMap()}
            serverIp={this.state.serverIp}
            subscriptionIndex={this.state.subscriptionIndex}
	            userRejectedUpgrade={()=>this.setState({paidTierAtInit:false})}
	            selectedPayment={this.state.selectedPayment}
	          />,
	          'account'
	        );
	      }
	      else if(this.state.pageIndex==="tutorial"){
	        return this.renderPageTransition(
	          <Tutorial
	            selectedLanguage={this.state.selectedLanguage}
	            closeTutorialWindow={()=>this.setState({pageIndex:''})}
	            holdTheMapStill={()=>this.holdTheMapStill()}
	            releaseTheMap={()=>this.releaseTheMap()}
	          />,
	          'tutorial'
	        );
	      }
	      else if(this.state.pageIndex==="stats"){
	        return this.renderPageTransition(
	          <MapEventGuardLegacy
	            as='div'
	            style={{ touchAction: 'pan-y' }}
          >
            <Stats
              selectedLanguage={this.state.selectedLanguage}
              closeTutorialWindow={()=>this.setState({pageIndex:''})}
              onOpenMunicipio={(slug)=>this.setState({pageIndex:'statsMunicipio', statsMunicipioSlug: slug})}
              holdTheMapStill={()=>this.holdTheMapStill()}
	              releaseTheMap={()=>this.releaseTheMap()}
	            />
	          </MapEventGuardLegacy>,
	          'stats'
	        );
	      }
	      else if(this.state.pageIndex==="statsMunicipio"){
	        return this.renderPageTransition(
	          <MapEventGuardLegacy
	            as='div'
	            style={{ touchAction: 'pan-y' }}
          >
            <MunicipioStats
              slug={this.state.statsMunicipioSlug}
              selectedLanguage={this.state.selectedLanguage}
              closeTutorialWindow={()=>this.setState({pageIndex:''})}
              onBackToIsland={()=>this.setState({pageIndex:'stats'})}
	            />
	          </MapEventGuardLegacy>,
	          'stats-municipio'
	        );
	      }
	      else if(this.state.pageIndex==="upgradePromotion"){
	        return this.renderPageTransition(
	          <Promo
	            selectedLanguage={this.state.selectedLanguage}
	            closePromoWindow={()=>this.setState({pageIndex:''})}
            coolDownStart={this.state.remainingCoolDown}
            closeTutorialWindow={()=>this.setState({pageIndex:''})}
            holdTheMapStill={()=>this.holdTheMapStill()}
	            releaseTheMap={()=>this.releaseTheMap()}
	            upgradeWindowPlus={()=>this.upgradeWindowPlus()}
	            upgradeWindowMax={()=>this.upgradeWindowMax()}
            upgradeWindowPass72={()=>this.upgradeWindowPass72()}
	          />,
	          'upgrade-promotion'
	        );
	      }
	    }

    upgradeWindowPlus(){
      trackBeginCheckout({
        plan: "Plus",
        source: "upgrade_promotion"
      });
      this.setState({pageIndex:'account',subscriptionIndex:'Plus'})
    }

    upgradeWindowMax(){
      trackBeginCheckout({
        plan: "PlusMAX",
        source: "upgrade_promotion"
      });
      this.setState({pageIndex:'account',subscriptionIndex:'PlusMAX'})
    }

    upgradeWindowPass72(){
      this.routeToPass72('upgrade_promotion');
    }

    // ⬇️ In App.jsx, REPLACE the whole karibeDataList(inData) with this version
    // ⬇️ REPLACE your whole karibeDataList(inData) with this
    karibeDataList(inData) {
      if (!inData) return null;

      let items = [];
      try { items = JSON.parse(inData); } catch { return null; }

      const openNew = (url) => {
        // Open the URL in a new tab/window without redirecting the current page.
        window.open(url, "_blank", "noopener,noreferrer");
      };
      

      return items.map((x, idx) => {
        const escrituraUrl = (x?.UrlArchivoPresentado || "").trim();
        const reciboUrl    = (x?.UrlArchivoRecibo || "").trim();
        const hasEscritura = !!escrituraUrl;
        const hasRecibo    = !!reciboUrl;
        const fecha        = (x?.FechaPresentacion || "").split(" ")[0] || "";
        const trans        = x?.TransaccionList || "Documento";

        const k = escrituraUrl || reciboUrl || `karibe-${idx}`;

        return (
          <li className="karibe-doc-card" key={k}>
            <div className="karibe-doc-title">
              <span className="karibe-doc-chip">{hasEscritura ? "Escritura" : "Recibo"}</span>
              <span className="karibe-doc-trans">{trans}</span>
            </div>

            <div className="karibe-doc-meta">
              <span className="karibe-doc-date" aria-label="Fecha de presentación">📅 {fecha}</span>
            </div>

            <div className="karibe-doc-actions">
              {hasEscritura && (
                <a
                  href={escrituraUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="karibe-doc-btn"
                  aria-label={`Abrir Escritura: ${trans}`}
                  tabIndex={-1}                                // avoid focus flash
                  onPointerDown={(e)=> e.stopPropagation()}    // don't let DOWN reach the map
                  onPointerUp={(e)=>{ e.preventDefault(); e.stopPropagation(); openNew(escrituraUrl); }}
                >
                  Abrir Escritura
                </a>
              )}

              {hasRecibo && (
                <a
                  href={reciboUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="karibe-doc-btn outline"
                  aria-label={`Abrir Recibo: ${trans}`}
                  tabIndex={-1}
                  onPointerDown={(e)=> e.stopPropagation()}
                  onPointerUp={(e)=>{ e.preventDefault(); e.stopPropagation(); openNew(reciboUrl); }}
                >
                  Abrir Recibo
                </a>
              )}
            </div>
          </li>
        );
      });
    }


  
    
   // ⬇️ In App.jsx, REPLACE the whole karibeDataShowOrHide(data) with this version
  karibeDataShowOrHide(data) {
    const raw = data?.attributes?.KARIBEDATA;
    if (!raw) return "";

    return (
         <MapEventGuardLegacy as="div" className="karibe-docs-guard karibeDataListContainer">
           <div className="karibe-docs-header">Documentos</div>
           <ul className="karibe-doc-list">{this.karibeDataList(raw)}</ul>
         </MapEventGuardLegacy>
      );

  }

    

    showExtraPointData(avgPriceSQFT,data){
      if(hasPlusMaxAccess(this.state.decodeToken)){
        return(
          <div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Comprador",'Buyer')}:</span>
              <span className='popupValue'>{data.attributes.CONTACT}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Vendedor",'Seller')}:</span>
              <span className='popupValue'>{data.attributes.SELLERNAME}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Valor intrinseco estimado",'Estimated Instrinsic Value')}:</span>
              <span className='popupValue popupValueNum'>{this.formatCurrency((avgPriceSQFT*data.attributes.STRUCTURE/10))}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Estimado de Equidad",'Equity Estimate')}:</span>
              <span className='popupValue popupValueNum'>{this.formatCurrency(((avgPriceSQFT*data.attributes.STRUCTURE/10)-data.attributes.SALESAMT))}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("CRIM estimado mens.",'Monthly Property Tax Estimate')}:</span>
              <span className='popupValue popupValueNum'>{this.formatCurrency((((data.attributes.TAXABLE*taxRates[data.attributes.MUNICIPIO])/12)*.9))}</span>
            </div>
            <div className='floodIndicator'>{this.state.floodZoneWarning}</div>
            {this.karibeDataShowOrHide(data)}
          </div>
        );
      }
      else if(this.checkIfUserIsPremium()&&isPass72(this.state.decodeToken)){
        return(
          <div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Comprador",'Buyer')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con PlusMAX",'Available with PlusMAX')}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Vendedor",'Seller')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con PlusMAX",'Available with PlusMAX')}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Valor intrinseco estimado",'Estimated Intrinsic Value')}:</span>
              <span className='popupValue popupValueNum'>{this.formatCurrency((avgPriceSQFT*data.attributes.STRUCTURE/10))}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Estimado de Equidad",'Equity Estimate')}:</span>
              <span className='popupValue popupValueNum'>{this.formatCurrency(((avgPriceSQFT*data.attributes.STRUCTURE/10)-data.attributes.SALESAMT))}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("CRIM estimado mens.",'Monthly Property Tax Estimate')}:</span>
              <span className='popupValue popupValueNum'>{this.formatCurrency((((data.attributes.TAXABLE*taxRates[data.attributes.MUNICIPIO])/12)*.9))}</span>
            </div>
            <div className='floodIndicator'>{this.state.floodZoneWarning}</div>
          </div>
        );
      }
      else if(this.checkIfUserIsPremium()&&this.state.decodeToken.delta==='497CC0000'){
        return(
          <div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Valor intrinseco estimado",'Estimated Instrinsic Value')}:</span>
              <span className='popupValue popupValueNum'>${(avgPriceSQFT*data.attributes.STRUCTURE/10).toFixed(2)}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Estimado de Equidad",'Equity Estimate')}:</span>
              <span className='popupValue popupValueNum'>${((avgPriceSQFT*data.attributes.STRUCTURE/10).toFixed(2)-data.attributes.SALESAMT).toFixed(2)}</span>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Comprador",'Buyer')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
              <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Vendedor",'Seller')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
              <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            </div>
          </div>
        );
      }
      else{
        return(
          <div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Comprador",'Buyer')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
              <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Vendedor",'Seller')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
              <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Valor intrinseco estimado",'Estimated Instrinsic Value')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
              <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            </div>
            <div className='popupContainer'>
              <span className='popupLabel'>{this.languageIndexer("Estimado de Equidad",'Equity Estimate')}:</span>
              <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
              <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
            </div>
          </div>
        );
      }
    }

    // handleMouseOver(event){
    //   try{
    //   if(this.state.decodeToken.delta==='11A5D7A0C'){
    //     this.setState({floodZoneWarning:''})
    //     let coords=event.target.getLatLng()
    //     fetch(`https://gis.jp.pr.gov/2013_RecibidorGeoComentarios/SometerGeoComentario/pnt_to_loc82_2022?ident=fromH_mipr2022&input_X=${coords.lng}&input_Y=${coords.lat}&isr=4326&osr=32161&method=`)
    //     .then(res=>res.json())
    //     .then(data=>{
    //       if(data.dato.atributos.Inun_Zona!=="X"){
    //         this.setState({floodZoneWarning:'ZONA INUNDABLE'})
    //       }
    //       else{
    //         this.setState({floodZoneWarning:''})
    //       }
    //   })}
    //   }catch{}
    // }


    handleMouseOver(event){
      try{
      if(hasPass72Access(this.state.decodeToken)){
        this.setState({floodZoneWarning:''})
        let coords=event.target.getLatLng()
        fetch(`https://gis.jp.pr.gov/2013_RecibidorGeoComentarios/SometerGeoComentario/pnt_to_loc82?input_X=${coords.lng}&input_Y=${coords.lat}&isr=4326&osr=32161&method=`)
        .then(res=>res.json())
        .then(data=>{
          if(data.results[0].attributes.Inun_Zona!=="X"){
            this.setState({floodZoneWarning:'ZONA INUNDABLE'})
          }
          else{
            this.setState({floodZoneWarning:''})
          }
      })}
      }catch{}
    }

    renderSearchBar(){
      if(this.state.decodeToken!==""){
        return this.renderDeferred(
          <Searchbar 
            selectedLanguage={this.state.selectedLanguage}
            searchPlaceholder={this.state.searchPlaceholder}
            holdTheMapStill={()=>this.holdTheMapStill()}
            releaseTheMap={()=>this.releaseTheMap()}
            sendDataToParent={data=>this.flyToLocation(data)}
          />
        );
      }
    }

	    termsWindow(){
	      if(this.state.pageIndex==='terms'){
	        return this.renderPageTransition(
	          <Terms
	            holdTheMapStill={()=>this.holdTheMapStill()}
	            releaseTheMap={()=>this.releaseTheMap()}
	            closeTerms={()=>this.setState({pageIndex:''})}
	          />,
	          'terms'
	        );
	      }
	      else if(this.state.pageIndex==='contact'){
	        return this.renderPageTransition(
	          <section
	          onTouchStart={()=>this.holdTheMapStill()}
	          onTouchEnd={()=>this.releaseTheMap()}
          closeTerms={()=>this.setState({pageIndex:''})}
          className='termsWindow'>
              <section className='contactInner' style={{maxWidth:700,}}>
                  <div className='closeHeader'>
                      <button style={{marginTop:10}} onClick={()=>this.setState({pageIndex:''})}>Cerrar</button>
                  </div>
                  <div className='contactStyling'>
	                      Para consultas contactanos escribiendo al email <strong>information@casadata.io</strong> con gusto le asistiremos!
	                  </div>
	              </section>
	          </section>,
	          'contact'
	        );
	      }
	      else if(this.state.pageIndex==='privacy'){
	        return this.renderPageTransition(
	          <Privacy
	            holdTheMapStill={()=>this.holdTheMapStill()}
	            releaseTheMap={()=>this.releaseTheMap()}
	            closeTerms={()=>this.setState({pageIndex:''})}
	          />,
	          'privacy'
	        );
	      }
	    }

    fetchTutorialPrompt(){
      let url=`${this.state.serverIp}/api/tutorialprompt`;
      let token = '';
      if(!localStorage.getItem('token')){
        token = sessionStorage.getItem('token')
      }
      else{
        token = localStorage.getItem('token')
      }
      fetch(url,{
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control':'no-cache',
          'Referrer':'https://casadata.io/',
          // Include the token in the Authorization header
          'Authorization': `Bearer ${token}`,
        },
        redirect: "follow", // manual, *follow, error
        // referrerPolicy:"no-referrer-when-downgrade",
      })
      .then(res=>res.json())
      .then(res=>{
        this.setState({userFirstLogin:res.tutorialprompt})
      })
    }

    fetchTrials(){
      let url=`${this.state.serverIp}/api/trialRegister`;
      let token = '';
      if(!localStorage.getItem('token')){
        token = sessionStorage.getItem('token')
      }
      else{
        token = localStorage.getItem('token')
      }
      fetch(url,{
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control':'no-cache',
          'Referrer':'https://casadata.io/',
          // Include the token in the Authorization header
          'Authorization': `Bearer ${token}`,
        },
        redirect: "follow", // manual, *follow, error
        // referrerPolicy:"no-referrer-when-downgrade",
      })
      .then(res=>res.json())
      .then(res=>{
        this.setState({trials:res.freesearches})
      })
    }

	    renderFirstLogin(){
	      if(this.state.userFirstLogin===false){
	        return this.renderPageTransition(
	          <Prompt
	            trials = {this.state.trials}
	            selectedLanguage={this.state.selectedLanguage}
            holdTheMapStill={()=>this.holdTheMapStill()}
            releaseTheMap={()=>this.releaseTheMap()}
            closePrompt={()=>this.fetchTutorialPrompt()}
            goToTutotial={()=>{
              this.setState({pageIndex:"tutorial"})
	              this.fetchTutorialPrompt()
	            }}
	          />,
	          'first-login-prompt'
	        )
	      }
	    }

	    renderTrialTutorialPrompt(){
	      if(this.state.trialTutorialFlag===true){
	        return this.renderPageTransition(
	          <TrialPromt
	            selectedLanguage={this.state.selectedLanguage}
	            holdTheMapStill={()=>this.holdTheMapStill()}
            releaseTheMap={()=>this.releaseTheMap()}
            closePrompt={()=>this.setState({trialTutorialFlag:false})}
	            goToTutotial={()=>{
	              this.setState({pageIndex:"tutorial"})
	            }}
	          />,
	          'trial-tutorial-prompt'
	        )
	      }
	    }

    formatCurrency(amount) {
      // Convert the number to a string with fixed two decimal points
      let formattedAmount = amount.toFixed(2);
      
      // Use regular expression to insert commas in the right places
      formattedAmount = formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  
      // Add the dollar sign at the beginning
      return `$${formattedAmount}`;
  }

    decideIfRenderVenta(salesamt){
      if(hasPass72Access(this.state.decodeToken)){
        return(<span className='popupValue popupValueNum'>{this.formatCurrency(salesamt)}</span>);
      }
      else{
        return(
          <>
            <span className='popupValue'>{this.languageIndexer("Disponible con memebresia",'Available with')}</span>
            <div className='levelShow2' style={{background:'#e1b12c',padding:3,color:'black',fontWeight:700,borderRadius:'5px'}}>PlusMAX</div>
          </>
        );
      }
    }

	    renderPlusMaxPrompt(){
	      if(this.state.pageIndex==='plusMaxPrompt'){
	        return this.renderPageTransition(
	          <PlusMaxPrompt
	            selectedLanguage={this.state.selectedLanguage}
	            holdTheMapStill={()=>this.holdTheMapStill()}
            releaseTheMap={()=>this.releaseTheMap()}
            closePlusMaxPrompt={()=>this.setState({pageIndex:''})}
            trials={this.state.trials}
            
            routeToPlusMax={()=>{
	              this.setState({pageIndex:''})
	              this.routeToPlusMax()
	            }}
            routeToPass72={()=>{
              this.setState({pageIndex:''})
              this.routeToPass72('plusmax_prompt')
            }}
	          />,
	          'plusmax-prompt'
	        )
	      }
	    }

    consumeTrials(){
      const token = this.state.token;      
      // this.setState({fetchingStatus:"fetching",maxPrice:9999999999,minPrice:0})
      let url = `${this.state.serverIp}/api/consumeTrials`
      if (window.location.protocol !== "file:"){
        fetch(url,{
          method: "POST", // *GET, POST, PUT, DELETE, etc.
          mode: "cors", // no-cors, *cors, same-origin
          cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
          credentials: "same-origin", // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control':'no-cache',
            'Referrer':'https://casadata.io/',
            // Include the token in the Authorization header
            'Authorization': `Bearer ${token}`,
          },
          redirect: "follow", // manual, *follow, error
          // referrerPolicy:"no-referrer-when-downgrade",
          // body: JSON.stringify(""), // body data type must match "Content-Type"
        })
        .then(res=>res.json())
        .then(() => {})
    }}

    languageIndexer(spanishMessage,englishMessage){
      if(this.state.selectedLanguage==='spanish'){return(spanishMessage);}
      else if(this.state.selectedLanguage==='english'){return(englishMessage);}
    }

    translateTransaction(transaction){
      if(transaction==='VENTA'){
        return('VENTA/SALE');
      }
      else if(transaction==='REPOSEIDA'){
        return('REPOSEIDA/REPOSSESSED');
      }
      else if(transaction==='REFINANCIAMIENTO'){
        return('REFINANCIAMIENTO/REFI');
      }
    }

    render() {
      const showMapWorkspace = this.state.loggedIn === true;
      const filteredDataRegister = showMapWorkspace ? this.getFilteredData() : [];
      const latestComparableSale = showMapWorkspace ? this.getLatestComparableSale(filteredDataRegister) : null;
      const latestSaleAmount = Number(latestComparableSale?.attributes?.SALESAMT);
      const latestSaleStructure = Number(latestComparableSale?.attributes?.STRUCTURE);
      const avgPriceSQFT = (
        Number.isFinite(latestSaleAmount) &&
        Number.isFinite(latestSaleStructure) &&
        latestSaleStructure > 0
      ) ? latestSaleAmount / (latestSaleStructure / 10) : 0;
      const safeSalesColorMax = showMapWorkspace ? this.getSalesColorScaleMax(filteredDataRegister) : 1;
      const repoDataFiltered = showMapWorkspace ? this.state.repoData.filter((data) => {
        if (!data || !data.attributes || this.state.toggleBg === 'toggleInactive') return false;
        if (transactionType(data.attributes) !== "REPOSEIDA") return false;
        const indexedDate = formatDateYYYYMMDD(data.attributes.SALESDTTM);
        const formatedDate = indexedDate ? new Date(indexedDate) : new Date(NaN);
        return (
          formatedDate.getTime() >= this.state.startDateRepo &&
          formatedDate.getTime() <= this.state.endDateRepo
        );
      }) : [];

	      return (
	        <div className={`App ${showMapWorkspace ? 'App--mapActive' : 'App--landingActive'}`}>
          {this.renderVerificationCoach()}
          {this.handleInformaticError()}
          {showMapWorkspace ? (
            <Suspense fallback={<div className='mapstyle' style={{ height: "100%", width: "100%" }} />}>
              <MapWorkspace
                mapRef={this.mapRef}
                initialCoords={this.state.initialCoords}
                initialZoomOut={this.state.initialZoomOut}
                onMoveEnd={(e) => this.trackMapViewport("moveend", e?.target)}
                onZoomEnd={(e) => this.trackMapViewport("zoomend", e?.target)}
                onDragEnd={(e) => this.trackMapViewport("dragend", e?.target)}
                onCreateShape={this._onCreate}
                onDeleteShape={() => this.handleDeleteShape()}
                pointsPaneClass={this.state.pointsPaneClass}
                closeBehavior={this.state.closeBehavior}
                decodeToken={this.state.decodeToken}
                filteredDataRegister={filteredDataRegister}
                llmRenderVersion={this.state.llmRenderVersion}
                safeSalesColorMax={safeSalesColorMax}
                avgPriceSQFT={avgPriceSQFT}
                repoDataFiltered={repoDataFiltered}
                markerRefs={this.markerRefs}
                searchBarNode={this.renderSearchBar()}
                gpsLocateNode={this.renderGpsLocateControl()}
                gpsFocusPoint={this.state.gpsFocusPoint}
                selectionMode={this.state.selectionMode}
                modeToggleNode={this.renderAutoModeHud()}
                autoExploreNode={(
                  <Suspense fallback={null}>
                    <AutoExplore
                    active={this.state.selectionMode === 'auto'}
                    suppressed={Boolean(this.state.autoComplex && !this.state.autoComplex.noMatch)}
                    serverIp={this.state.serverIp}
                    token={this.state.token}
                    complexLoading={this.state.autoComplexLoading}
                    onFetchStateChange={(loading) => {
                      if (this.state.autoPointsLoading !== Boolean(loading)) {
                        this.setState({ autoPointsLoading: Boolean(loading) });
                      }
                    }}
                    languageIndexer={(spanish, english) => this.languageIndexer(spanish, english)}
                    onSelectComplex={(feature) => this.handleAutoSelectComplex(feature)}
                    isCatastroSelected={(catastro) => Boolean(this.autoSelectedCatastros?.has(String(catastro || '').trim()))}
                    isCatastroNoMatch={(catastro) => Boolean(this.autoNoMatchCatastros?.has(String(catastro || '').trim()))}
                    />
                  </Suspense>
                )}
                toggleSearchBtnNode={this.toggleSearchBtn()}
                numericLimitsNode={this.renderNumericLimits()}
                numericLimitsRepoNode={this.renderNumericLimitsRepo()}
                statsNode={this.displayOrHideStats(filteredDataRegister)}
                loginBtnsNode={this.renderLoginBtns()}
                loginPagesNode={this.renderLoginPages()}
                logOutBtnNode={this.logOutBtn()}
                accountPageNode={this.accountPage()}
                memberBoxNode={this.renderMemberBox()}
                termsWindowNode={this.termsWindow()}
                firstLoginNode={this.renderFirstLogin()}
                trialTutorialPromptNode={this.renderTrialTutorialPrompt()}
                plusMaxPromptNode={this.renderPlusMaxPrompt()}
                areaPulseNode={this.renderAreaPulse()}
                drawFeatureGroupRef={this.drawFeatureGroupRef}
                getSalesAmtColor={(salesAmt, max) => this.getSalesAmtColor(salesAmt, max)}
                onSaleMarkerHover={(e, data) => {
                  this.handleMouseOver(e);
                  this.trackPropertyPreview(data);
                }}
                languageIndexer={(spanish, english) => this.languageIndexer(spanish, english)}
                decideIfRenderVenta={(salesamt) => this.decideIfRenderVenta(salesamt)}
                formatDireccion={(address) => this.formatDireccion(address)}
                translateTransaction={(transaction) => this.translateTransaction(transaction)}
                showExtraPointData={(avg, data) => this.showExtraPointData(avg, data)}
                formatCurrency={(amount) => this.formatCurrency(amount)}
              />
            </Suspense>
          ) : (
            <div className='mapstyle' style={{ height: "100vh", width: "100%" }}>
              {this.renderLoginBtns()}
              {this.renderLoginPages()}
              {this.termsWindow()}
              {this.renderFirstLogin()}
              {this.renderTrialTutorialPrompt()}
              {this.renderPlusMaxPrompt()}
            </div>
          )}
        </div>
      );
    }


  }
  export default App;

